import { ChecklistFieldAccess } from '@/components/checklist/ChecklistFieldAccess';
import { PartnerChecklistModal } from '@/components/checklist/PartnerChecklistModal';
import { usePartnerChecklist, hasPartnerChecklistPending } from '@/hooks/usePartnerChecklist';
import { callPartnerOwner, newPartnerRequestId, partnerOwnerErrorMessage } from '@/services/partnerChecklistOwnerService';
import { readPartnerChecklistBaseline } from '@/services/checklist/partnerChecklistBaseline';
import { parsePartnerStorageReference } from '@/utils/media/partnerStorageReference';
import { useChecklistSaveError } from '@/hooks/useChecklistSaveError';
import { useChecklistLeaveGuard } from '@/hooks/useChecklistLeaveGuard';
import { useChecklistObservationCapability } from '@/hooks/useChecklistObservationCapability';
import { mergeChecklistDraftRefresh } from '@/services/checklist/ChecklistDraftPersistence';
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, BackHandler, Keyboard, ActivityIndicator, Alert, Platform, ScrollView, useWindowDimensions, Modal, Clipboard, Linking } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useFocusEffect, useNavigation } from 'expo-router';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import QRCode from 'react-native-qrcode-svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import theme from '../../../config/theme';
import { ChecklistFieldsForm, ChecklistFieldsFormHandle } from '../../../components/checklist/ChecklistFieldsForm';
import { useChecklistEditor, Checklist, ChecklistField } from '../../../hooks/useChecklistEditor';
import { useChecklistHistoricalRead } from '@/hooks/useChecklistHistoricalRead';
import { observer, useObservable } from '@legendapp/state/react';
import { showToast } from '../../../utils/toast';
import { databaseService } from '../../../services/databaseService';
import { useWebAlert } from '../../../components/WebAlert';
import { APP_CONSTANTS } from '@/config/constants';
import { IdChecklistLogger } from '../../../utils/logger';
import { debugPause } from '@/utils/debugPauseUniversal';
import { ChecklistActionsHelper } from '../../../services/checklist/ChecklistActionsHelper';
import { globalState } from '@/state/globalState';
import { AppNavigationLink } from '@/components/navigation/AppNavigationLeave';
import { ChecklistActionsTip } from '@/components/onboarding/ChecklistActionsTip';
import { countChecklistFilledFields } from '@/services/checklist/checklistLayout';
import { hasDeveloperBypass, hasPremiumAccess } from '@/utils/subscriptionAccess';
import UpgradePrompt from '@/components/subscription/UpgradePrompt';
import { realtimeTrigger } from '@/state/realtime';
import {
  createChecklistShareLink,
  getChecklistShareStatus,
  revokeChecklistShareLink,
  ChecklistShareLinkStatus,
  ChecklistShareScopeMode,
} from '@/services/checklistShareService';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import { canAccessChecklistDashboard, canDeleteChecklistForRole, canEditChecklistForRole, canViewChecklistForRole, getUserTypeLabel } from '@/utils/userRoles';
import { canTransferChecklist } from '@/utils/checklistAssignment';
import { checklistTransferService } from '@/services/checklist/ChecklistTransferRuntime';
import {
  ChecklistTransferError,
  type ChecklistTransferChecklist,
} from '@/services/checklist/ChecklistTransferService';
import type { ChecklistAssignmentCandidate } from '@/utils/checklistAssignment';
import { CHECKLIST_UNSAVED_MARKS_MESSAGE } from '@/services/checklist/checklistMarking';
import {
  buildChecklistMarkTipMessage,
  buildChecklistMarkTipStorageKey,
  persistChecklistMarkTipDismissed,
  readChecklistMarkTipDismissed,
} from '@/services/checklist/checklistMarkingTip';
// Importação condicional para geração de PDF

const IMAGE_FIELD_TYPES = new Set(['foto', 'photo', 'assinatura', 'signature', 'marcação', 'marcacao', 'mark']);
const isRemoteImageValue = (value?: string | null) => Boolean(value && (/^https?:\/\//.test(value) || parsePartnerStorageReference(value)));

const getTransferRecipientRoleLabel = (userType?: string | null): string => {
  const label = getUserTypeLabel(userType);
  return label === 'Administrador' || label === 'Motorista' ? label : 'Responsável';
};

const getTransferRecipientName = (candidate: ChecklistAssignmentCandidate): string =>
  candidate.name?.trim() || 'Responsável';

const SHARE_TTL_MINUTES = 20;
const SHARE_MAX_UPDATES = 10;
const SHARE_PUBLIC_FIELD_TYPES = new Set([
  'text',
  'number',
  'timestamp with time zone',
  'mensagem pronta',
  'cep1',
  'cep2',
  'cep3',
  'cep4',
  'cep5',
  'cep6',
]);

type ChecklistShareSession = {
  shareLinkId: string;
  checklistId: string;
  status: ChecklistShareLinkStatus;
  expiresAt: string;
  url: string;
  token: string;
  ttlMinutes: number;
  maxUpdates: number | null;
  updatesCount: number;
  expiresInSeconds: number;
  lastActivityAt: string | null;
  revokedAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  scopeMode: ChecklistShareScopeMode;
  allowedFieldIds: string[] | null;
};

type ShareFlowModalKind = 'share' | 'client_control' | 'client_completed' | null;

type ShareScopeFieldCandidate = {
  id: string;
  grupo: string;
  nome: string;
  ordem: number;
};

type ShareScopeGroup = {
  grupo: string;
  fields: ShareScopeFieldCandidate[];
};

const getSecondsRemaining = (expiresAtIso: string, nowMs: number): number => {
  const expiresAtMs = new Date(expiresAtIso).getTime();
  if (Number.isNaN(expiresAtMs)) {
    return 0;
  }
  return Math.max(0, Math.floor((expiresAtMs - nowMs) / 1000));
};

const formatCountdown = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return 'Ainda sem atividade';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Data inválida';
  }

  return parsed.toLocaleString('pt-BR');
};

const getShareStatusLabel = (status: ChecklistShareLinkStatus): string => {
  if (status === 'active') return 'Ativo';
  if (status === 'revoked') return 'Revogado';
  if (status === 'expired') return 'Expirado';
  return 'Concluído';
};

const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return 'Erro inesperado';
};

const buildWhatsAppShareUrl = (publicUrl: string): string => `https://wa.me/?text=${encodeURIComponent(publicUrl)}`;

const isFieldVisibleForShare = (field: ChecklistField): boolean => {
  const visivel = field.visivel;
  if (typeof visivel === 'boolean') return visivel;
  if (typeof visivel === 'number') return visivel !== 0;
  if (typeof visivel === 'string') {
    const normalized = visivel.trim().toLowerCase();
    return normalized !== 'false' && normalized !== '0';
  }
  return true;
};

const isPublicShareFieldType = (fieldType: string): boolean => {
  if (SHARE_PUBLIC_FIELD_TYPES.has(fieldType)) {
    return true;
  }
  return fieldType.startsWith('dropdown');
};

const ChecklistEditorContent = observer(() => {
  const router = useRouter();
  const navigation = useNavigation<any>();
  const { id, returnTo, recoveredPhotoFieldId } = useLocalSearchParams();
  const safeAreaInsets = useSafeAreaInsets();
  const subscriptionSnapshot = globalState.subscription.get();
  const currentUserId = globalState.user.user.get()?.id || null;
  const currentUserType = globalState.user.profile.get()?.user_type;
  const currentEmpresaId = globalState.user.profile.get()?.empresa_id || null;
  const isOnline = globalState.network.isConnected.get();
  const realtimeCounter = realtimeTrigger.counter.get();
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const editorIdentity = `${String(id)}:${currentUserId}:${currentEmpresaId}`;
  const [pending, setPending] = useState(false);
  const [hasUnsavedMarks, setHasUnsavedMarks] = useState(false);
  const [groupExpansion, setGroupExpansion] = useState({ allExpanded: true, allCollapsed: false, captureBusy: false });
  const { width: windowWidth, height: windowHeight, fontScale } = useWindowDimensions();

  const {
    checklist,
    template: currentTemplate,
    templateRetired,
    values: currentValues,
    valuesReadVersion,
    markValuesWriteConfirmed,
    loading: editorLoading,
    error: editorError,
    refetch,
  } = useChecklistEditor(String(id), {
    syncRevision: realtimeCounter,
    syncEnabled: isEditorFocused && !!currentUserId && !!currentEmpresaId && !pending && !hasUnsavedMarks && !groupExpansion.captureBusy,
    identityKey: editorIdentity,
    companyId: currentEmpresaId,
  });
  const observationsEnabled = useChecklistObservationCapability({
    companyId: checklist?.empresa_id,
    userId: currentUserId,
    profileCompanyId: currentEmpresaId,
    checklistId: checklist?.id,
    isOnline,
  }) === true;
  const { historicalRead, historicalReadError, historicalReadReady } = useChecklistHistoricalRead({
    checklist, currentFields: currentTemplate, currentValues, templateRetired, valuesReadVersion, identity: editorIdentity,
    paused: pending || hasUnsavedMarks || groupExpansion.captureBusy,
  });
  const draft = useObservable<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useChecklistSaveError(hasUnsavedMarks);
  const bypassRemovalRef = React.useRef(false);
  const leaveFocusRef = React.useRef({ focused: false, generation: 0 });
  const leaveIdentityRef = React.useRef('');
  const leaveIdentity = `${String(id)}:${currentUserId}:${currentEmpresaId}`;
  if (leaveIdentityRef.current !== leaveIdentity) {
    leaveIdentityRef.current = leaveIdentity;
    leaveFocusRef.current.generation += 1;
  }
  useFocusEffect(useCallback(() => {
    leaveFocusRef.current.focused = true;
    leaveFocusRef.current.generation += 1;
    return () => {
      leaveFocusRef.current.focused = false;
      leaveFocusRef.current.generation += 1;
    };
  }, []));
  const unobservedConfirmedRef = React.useRef<Record<string, string>>({});
  const confirmedWriteVersionRef = React.useRef(0);
  const [loadingAction, setLoadingAction] = useState<'complete' | 'delete' | 'pdf' | 'reopen' | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [shareFlowModalKind, setShareFlowModalKind] = useState<ShareFlowModalKind>(null);
  const [shareSession, setShareSession] = useState<ChecklistShareSession | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareCreating, setShareCreating] = useState(false);
  const [shareRefreshing, setShareRefreshing] = useState(false);
  const [shareRevoking, setShareRevoking] = useState(false);
  const [shareScopeError, setShareScopeError] = useState<string | null>(null);
  const [shareScopeCollapsed, setShareScopeCollapsed] = useState(false);
  const [shareSelectedFieldIds, setShareSelectedFieldIds] = useState<string[]>([]);
  const [shareScopeExpandedGroups, setShareScopeExpandedGroups] = useState<Record<string, boolean>>({});
  const [shareCopyFeedbackVisible, setShareCopyFeedbackVisible] = useState(false);
  const [shareNowTick, setShareNowTick] = useState<number>(Date.now());
  const [clientControlActive, setClientControlActive] = useState(false);
  const [clientControlBusy, setClientControlBusy] = useState(false);
  const [clientActivityMessage, setClientActivityMessage] = useState<string | null>(null);
  const [clientCompletionMessage, setClientCompletionMessage] = useState<string | null>(null);
  const [transferFeatureEnabled, setTransferFeatureEnabled] = useState(false);
  const [transferModalKind, setTransferModalKind] = useState<'list' | 'confirm' | null>(null);
  const [transferCandidates, setTransferCandidates] = useState<ChecklistAssignmentCandidate[]>([]);
  const [transferTarget, setTransferTarget] = useState<ChecklistAssignmentCandidate | null>(null);
  const [transferOwnerName, setTransferOwnerName] = useState('Responsável atual');
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  // Ref para marcar o início do ciclo de digitação
  const typingStartRef = React.useRef<number | null>(null);
  // Ref para guardar o último valor de pending
  const lastPendingRef = React.useRef<boolean>(false);
  const lastSyncedDraftRef = React.useRef<Record<string, string>>({});
  const shareSessionRef = React.useRef<ChecklistShareSession | null>(null);
  const shareFlowModalKindRef = React.useRef<ShareFlowModalKind>(null);
  const clientControlActiveRef = React.useRef(false);
  const shareScopeChecklistRef = React.useRef<string | null>(null);
  const shareCopyFeedbackTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const markTipAlertUserRef = React.useRef<string | null>(null);
  const { showAlert } = useWebAlert();
  const [pdfCacheUri, setPdfCacheUri] = useState<string | undefined>();
  const formRef = React.useRef<ChecklistFieldsFormHandle>(null);
  const [partnerModalVisible, setPartnerModalVisible] = useState(false);
  useFocusEffect(useCallback(() => {
    setIsEditorFocused(true);
    return () => setIsEditorFocused(false);
  }, []));
  const partnerScope = { companyId: currentEmpresaId || '', userId: currentUserId || '', checklistId: checklist?.id || '' };
  const partner = usePartnerChecklist({ ...partnerScope, isOnline, isFocused: isEditorFocused, editing: pending || hasUnsavedMarks || groupExpansion.captureBusy, onRefetch: refetch });
  const preparePartnerShare = useCallback(async () => {
    if (!isOnline) throw new Error('Conecte-se à internet antes de compartilhar.');
    if (!formRef.current) throw new Error('Aguarde o formulário carregar.');
    await formRef.current.flushPending();
    const { syncAll } = await import('@/services/syncService');
    await syncAll();
  }, [isOnline]);

  const recoveredPhotoScrollRequestRef = React.useRef<string | null>(null);
  const photoBackGuardUntilRef = React.useRef(0);
  const initialFocusHandledRef = React.useRef(false);
  const photoBackGuardStateRef = React.useRef<{
    fieldId: string;
    active: boolean;
    reason: string;
    at: number;
    guardUntil: number;
  } | null>(null);
  const template = historicalReadReady && historicalRead ? historicalRead.fields : currentTemplate;
  const values = historicalReadReady && historicalRead ? historicalRead.values : currentValues;
  const loading = editorLoading || (!!checklist && !historicalReadReady && !historicalReadError);
  const error = editorError || historicalReadError;
  useEffect(() => {
    let active = true;
    setTransferFeatureEnabled(false);
    setTransferCandidates([]);
    setTransferTarget(null);
    setTransferError(null);
    setTransferModalKind(null);
    setTransferOwnerName('Responsável atual');

    if (!checklist?.empresa_id || !checklist.usuario_id) {
      return () => { active = false; };
    }

    void Promise.all([
      checklistTransferService.isFeatureEnabled(checklist.empresa_id).catch(() => false),
      databaseService
        .readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, checklist.usuario_id)
        .catch(() => null),
    ]).then(([enabled, user]) => {
      if (!active) return;
      setTransferFeatureEnabled(enabled);
      const name = user?.name?.trim();
      if (name) setTransferOwnerName(name);
    });

    return () => { active = false; };
  }, [checklist?.empresa_id, checklist?.id, checklist?.usuario_id, isOnline]);
  const [editorMenu, setEditorMenu] = useState<'actions' | 'app' | null>(null);
  const actionsAnchorRef = React.useRef<View | null>(null);
  const actionsTipHostRef = React.useRef<View | null>(null);
  useEffect(() => {
    if (Platform.OS !== 'web' || !editorMenu || typeof document === 'undefined') return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setEditorMenu(null); }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [editorMenu]);
  const isShareFlowModalVisible = shareFlowModalKind !== null;
  const safeTopInset = Platform.OS === 'web' ? 0 : safeAreaInsets.top;
  const topBarPaddingTop = Math.max(8, safeTopInset + theme.spacing.xs);
  const recoveredPhotoFieldIdParam = useMemo(() => {
    if (Array.isArray(recoveredPhotoFieldId)) {
      return recoveredPhotoFieldId[0] || null;
    }
    return recoveredPhotoFieldId || null;
  }, [recoveredPhotoFieldId]);
  const fieldTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    template?.forEach(field => map.set(field.id, field.tipo));
    return map;
  }, [template]);
  const shareScopeFields = useMemo<ShareScopeFieldCandidate[]>(() => {
    if (!template || template.length === 0) {
      return [];
    }

    return template
      .filter(field => isFieldVisibleForShare(field) && isPublicShareFieldType(field.tipo))
      .map(field => ({
        id: field.id,
        grupo: field.grupo || 'Geral',
        nome: field.nome || 'Campo sem nome',
        ordem: Number(field.ordem ?? 0),
      }))
      .sort((a, b) => {
        if (a.grupo === b.grupo) {
          if (a.ordem !== b.ordem) {
            return a.ordem - b.ordem;
          }
          return a.nome.localeCompare(b.nome, 'pt-BR');
        }
        return a.grupo.localeCompare(b.grupo, 'pt-BR');
      });
  }, [template]);
  const shareScopeGroups = useMemo<ShareScopeGroup[]>(() => {
    const groups = new Map<string, ShareScopeFieldCandidate[]>();
    shareScopeFields.forEach(field => {
      const groupKey = field.grupo || 'Geral';
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(field);
    });

    return Array.from(groups.entries()).map(([grupo, fields]) => ({
      grupo,
      fields,
    }));
  }, [shareScopeFields]);
  const shareScopeFieldIds = useMemo(() => shareScopeFields.map(field => field.id), [shareScopeFields]);

  const handlePhotoFlowStateChange = useCallback((state: {
    fieldId: string;
    active: boolean;
    reason: string;
    at: number;
  }) => {
    const guardMs = state.active ? 30000 : 5000;
    const guardUntil = Date.now() + guardMs;

    photoBackGuardUntilRef.current = guardUntil;
    photoBackGuardStateRef.current = {
      ...state,
      guardUntil,
    };

    IdChecklistLogger.Main.info(
      `[CHECKLIST-EDITOR] photoBackGuard atualizado campo=${state.fieldId} ativo=${state.active} motivo=${state.reason} guardMs=${guardMs}`
    );
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'android' || !recoveredPhotoFieldIdParam || !template?.length) {
      return;
    }

    const requestKey = `${String(id)}:${recoveredPhotoFieldIdParam}`;
    if (recoveredPhotoScrollRequestRef.current === requestKey) {
      return;
    }

    const targetField = template.find(field => field.id === recoveredPhotoFieldIdParam);
    if (!targetField) {
      IdChecklistLogger.Main.warn(
        `[CHECKLIST-EDITOR] campo de foto recuperada não encontrado no template: ${recoveredPhotoFieldIdParam}`
      );
      return;
    }

    recoveredPhotoScrollRequestRef.current = requestKey;

    setTimeout(() => {
      formRef.current?.scrollToField(recoveredPhotoFieldIdParam);
    }, 250);
  }, [id, recoveredPhotoFieldIdParam, template]);

  useEffect(() => {
    shareSessionRef.current = shareSession;
  }, [shareSession]);

  useEffect(() => {
    shareFlowModalKindRef.current = shareFlowModalKind;
  }, [shareFlowModalKind]);

  useEffect(() => {
    clientControlActiveRef.current = clientControlActive;
  }, [clientControlActive]);

  const transitionShareFlowModal = useCallback((nextKind: ShareFlowModalKind, reason: string) => {
    const previousKind = shareFlowModalKindRef.current;
    if (previousKind === nextKind) {
      IdChecklistLogger.Main.info(
        `[shareFlowModal] mantendo=${nextKind || 'none'} motivo=${reason}`
      );
      return;
    }

    if (nextKind !== 'share') {
      setShareCopyFeedbackVisible(false);
    }

    IdChecklistLogger.Main.info(
      `[shareFlowModal] ${previousKind || 'none'} -> ${nextKind || 'none'} motivo=${reason}`
    );
    shareFlowModalKindRef.current = nextKind;
    setShareFlowModalKind(nextKind);
  }, []);

  useEffect(() => {
    return () => {
      if (shareCopyFeedbackTimerRef.current) {
        clearTimeout(shareCopyFeedbackTimerRef.current);
        shareCopyFeedbackTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const checklistId = checklist?.id || null;
    const scopeFields = shareScopeFields;
    const scopeFieldIds = scopeFields.map(field => field.id);
    const scopeFieldSet = new Set(scopeFieldIds);
    const checklistChanged = shareScopeChecklistRef.current !== checklistId;
    shareScopeChecklistRef.current = checklistId;

    if (!checklistId || scopeFieldIds.length === 0) {
      setShareSelectedFieldIds([]);
      setShareScopeExpandedGroups({});
      return;
    }

    setShareSelectedFieldIds(prev => {
      if (checklistChanged) {
        return scopeFieldIds;
      }

      const filtered = prev.filter(id => scopeFieldSet.has(id));
      if (filtered.length === 0) {
        return scopeFieldIds;
      }

      if (filtered.length !== prev.length) {
        return filtered;
      }

      return prev;
    });

    if (checklistChanged) {
      setShareScopeExpandedGroups({});
    }

    setShareScopeError(null);
  }, [checklist?.id, shareScopeFields]);

  const shareSelectedFieldIdsSet = useMemo(() => new Set(shareSelectedFieldIds), [shareSelectedFieldIds]);
  const shareSelectedScopeFields = useMemo(
    () => shareScopeFields.filter(field => shareSelectedFieldIdsSet.has(field.id)),
    [shareScopeFields, shareSelectedFieldIdsSet]
  );
  const shareSelectedScopeFieldIdsOrdered = useMemo(
    () => shareSelectedScopeFields.map(field => field.id),
    [shareSelectedScopeFields]
  );
  const shareScopeTotalFields = shareScopeFieldIds.length;
  const shareScopeSelectedCount = shareSelectedScopeFieldIdsOrdered.length;
  const shareHasCustomScope =
    shareScopeSelectedCount > 0 && shareScopeSelectedCount < shareScopeTotalFields;
  const canTransferCurrentChecklist = !!checklist && canTransferChecklist({
    actorId: currentUserId,
    actorCompanyId: currentEmpresaId,
    actorUserType: currentUserType,
    ownerId: checklist.usuario_id,
    checklistCompanyId: checklist.empresa_id,
  });
  // Offline o botão continua visível para explicar por que a ação não pode prosseguir.
  const canOpenTransfer = canTransferCurrentChecklist && (transferFeatureEnabled || !isOnline);
  const canEditChecklistRole = canEditChecklistForRole({
    userType: currentUserType,
    checklistUsuarioId: checklist?.usuario_id ?? null,
    checklistEmpresaId: checklist?.empresa_id ?? null,
    currentUserId,
    currentEmpresaId,
  });
  const canReopenChecklist = Boolean(
    canEditChecklistRole &&
    checklist?.status === 'done' &&
    checklist?.retired_at == null
  );
  const canDeleteChecklist = Boolean(checklist) && canDeleteChecklistForRole({
    userType: currentUserType,
    checklistUsuarioId: checklist?.usuario_id ?? null,
    checklistEmpresaId: checklist?.empresa_id ?? null,
    currentUserId,
    currentEmpresaId,
    checklistRetiredAt: checklist?.retired_at,
  });

  const sanitizeDraftForPersistence = useCallback((data: Record<string, string>) => {
    const cleaned: Record<string, string> = {};
    const skipped: string[] = [];

    Object.entries(data).forEach(([campoId, valor]) => {
      const tipo = fieldTypeMap.get(campoId);
      const isImageField = tipo ? IMAGE_FIELD_TYPES.has(tipo) : false;

      if (!isImageField) {
        cleaned[campoId] = valor;
        return;
      }

      if (valor === '' || isRemoteImageValue(valor)) {
        cleaned[campoId] = valor;
        return;
      }

      skipped.push(campoId);
    });

    if (skipped.length > 0) {
      IdChecklistLogger.Main.info(`[CHECKLIST-EDITOR] sanitizeDraft - ignorando campos de imagem locais: ${skipped.join(', ')}`);
    }

    return { cleaned, skipped };
  }, [fieldTypeMap]);

  const resolvedReturnTo = Array.isArray(returnTo) ? returnTo[0] : returnTo;
  const desktopBreakpoint = APP_CONSTANTS.UI?.DESKTOP_BREAKPOINT ?? 1024;
  const isDesktopLayout = Platform.OS === 'web' && windowWidth >= desktopBreakpoint;

  const navigateBack = useCallback(() => {
    if (typeof resolvedReturnTo === 'string' && resolvedReturnTo.trim().length > 0) {
      if (isDesktopLayout && typeof router.canGoBack === 'function' && router.canGoBack()) {
        router.back();
        return;
      }
      router.replace(resolvedReturnTo);
      return;
    }
    if (router.canGoBack()) router.back();
    else router.replace('/(app)');
  }, [checklist?.id, desktopBreakpoint, isDesktopLayout, resolvedReturnTo, router, windowWidth]);

  const checklistMarkTipStorageKey = useMemo(
    () => buildChecklistMarkTipStorageKey(currentUserId),
    [currentUserId]
  );

  const handleMarkInteraction = useCallback((kind: 'tap' | 'drag') => {
    const dismissed = readChecklistMarkTipDismissed(checklistMarkTipStorageKey);
    if (kind === 'tap' && dismissed) return true;
    if (markTipAlertUserRef.current === checklistMarkTipStorageKey) return false;
    markTipAlertUserRef.current = checklistMarkTipStorageKey;
    void showAlert(
      'A marcação mudou',
      buildChecklistMarkTipMessage(observationsEnabled),
      [{
        text: 'OK',
        onPress: () => persistChecklistMarkTipDismissed(checklistMarkTipStorageKey),
      }]
    ).catch(() => {}).finally(() => {
      if (markTipAlertUserRef.current === checklistMarkTipStorageKey) markTipAlertUserRef.current = null;
    });
    return false;
  }, [checklistMarkTipStorageKey, observationsEnabled, showAlert]);

  const showMessage = (title: string, msg: string, buttons?: any[]) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, buttons || [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg, buttons);
    }
  };

  const shareSecondsRemaining = useMemo(() => {
    if (!shareSession) {
      return 0;
    }
    if (shareSession.status !== 'active') {
      return 0;
    }
    return getSecondsRemaining(shareSession.expiresAt, shareNowTick);
  }, [shareNowTick, shareSession]);

  const hydrateShareSessionFromStatus = useCallback(
    (payload: Awaited<ReturnType<typeof getChecklistShareStatus>>, baseSession?: ChecklistShareSession | null) => {
      const shareLink = payload.share_link;
      const previous = baseSession ?? shareSessionRef.current;
      const nextSession: ChecklistShareSession = {
        shareLinkId: shareLink.id,
        checklistId: shareLink.checklist_id,
        status: shareLink.status,
        expiresAt: shareLink.expires_at,
        expiresInSeconds: shareLink.expires_in_seconds,
        updatesCount: shareLink.updates_count,
        maxUpdates: shareLink.max_updates,
        lastActivityAt: shareLink.last_activity_at,
        revokedAt: shareLink.revoked_at,
        completedAt: shareLink.completed_at,
        createdAt: shareLink.created_at,
        updatedAt: shareLink.updated_at,
        url: previous?.url || '',
        token: previous?.token || '',
        ttlMinutes: previous?.ttlMinutes || SHARE_TTL_MINUTES,
        scopeMode: shareLink.scope_mode || previous?.scopeMode || 'template_default',
        allowedFieldIds:
          (shareLink.scope_mode || previous?.scopeMode || 'template_default') === 'custom_fields'
            ? (Array.isArray(shareLink.allowed_field_ids) && shareLink.allowed_field_ids.length > 0
                ? shareLink.allowed_field_ids
                : previous?.allowedFieldIds || null)
            : null,
      };

      const hadPreviousSession = !!previous;
      const isStillActive = shareLink.status === 'active' && previous?.status === 'active';
      const transitionedToCompleted = hadPreviousSession && previous?.status === 'active' && shareLink.status === 'completed';
      const updatesIncreased = (shareLink.updates_count || 0) > (previous?.updatesCount || 0);
      const activityChanged =
        !!shareLink.last_activity_at &&
        shareLink.last_activity_at !== (previous?.lastActivityAt || null);
      const clientActivityDetected = hadPreviousSession && isStillActive && (updatesIncreased || activityChanged);

      if (clientActivityDetected) {
        const updatesDelta = Math.max(0, (shareLink.updates_count || 0) - (previous?.updatesCount || 0));
        const isFirstOpen = !previous?.lastActivityAt && !!shareLink.last_activity_at;
        const message = isFirstOpen
          ? 'Cliente abriu o checklist público e pode estar preenchendo agora.'
          : updatesDelta > 0
          ? `Cliente enviou ${updatesDelta} atualização(ões).`
          : 'Cliente interagiu com a sessão pública.';

        setClientActivityMessage(message);
        setClientControlActive(true);
        const currentModalKind = shareFlowModalKindRef.current;
        if (currentModalKind === 'share' || currentModalKind === 'client_control') {
          // Reaproveita o mesmo Modal nativo do fluxo de QR para evitar colisão no iOS.
          transitionShareFlowModal('client_control', 'client_activity_detected');
        } else {
          IdChecklistLogger.Main.info(
            `[CHECKLIST-EDITOR] Atividade do cliente detectada com fluxo QR fechado; mantendo banner de controle. shareLinkId=${shareLink.id}`
          );
        }
        IdChecklistLogger.Main.info(
          `[CHECKLIST-EDITOR] Atividade do cliente detectada: ${message} shareLinkId=${shareLink.id}`
        );
      }

      if (transitionedToCompleted) {
        const completionMessage = 'Cliente concluiu as alterações do checklist.';
        setClientCompletionMessage(completionMessage);
        setClientControlActive(false);
        setClientActivityMessage(null);
        const currentModalKind = shareFlowModalKindRef.current;
        if (
          currentModalKind === 'share' ||
          currentModalKind === 'client_control' ||
          clientControlActiveRef.current
        ) {
          transitionShareFlowModal('client_completed', 'client_completed_detected');
        } else {
          IdChecklistLogger.Main.info(
            `[CHECKLIST-EDITOR] Cliente concluiu alterações com fluxo QR fechado; mantendo notificação sem abrir novo modal. shareLinkId=${shareLink.id}`
          );
        }
        showToast('Cliente concluiu alterações. Checklist liberado.', { duration: 2200, type: 'success' });
        IdChecklistLogger.Main.info(
          `[CHECKLIST-EDITOR] Sessão pública concluída pelo cliente. shareLinkId=${shareLink.id}`
        );
      }

      if (shareLink.status !== 'active') {
        setClientControlActive(false);
        setClientActivityMessage(null);
        if (!transitionedToCompleted && shareFlowModalKindRef.current === 'client_control') {
          transitionShareFlowModal('share', 'share_status_not_active');
        }
      }

      setShareSession(nextSession);
    },
    [transitionShareFlowModal]
  );

  const toggleShareScopeGroupExpanded = useCallback((groupName: string) => {
    setShareScopeExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  }, []);

  const toggleShareScopeFieldSelection = useCallback((fieldId: string) => {
    setShareSelectedFieldIds(prev => {
      const nextSet = new Set(prev);
      if (nextSet.has(fieldId)) {
        nextSet.delete(fieldId);
      } else {
        nextSet.add(fieldId);
      }
      return Array.from(nextSet);
    });
    setShareScopeError(null);
  }, []);

  const setShareScopeGroupSelection = useCallback((group: ShareScopeGroup, selected: boolean) => {
    setShareSelectedFieldIds(prev => {
      const nextSet = new Set(prev);
      group.fields.forEach(field => {
        if (selected) {
          nextSet.add(field.id);
        } else {
          nextSet.delete(field.id);
        }
      });
      return Array.from(nextSet);
    });
    setShareScopeError(null);
  }, []);

  const handleSelectAllShareScopeFields = useCallback(() => {
    setShareSelectedFieldIds(shareScopeFieldIds);
    setShareScopeError(null);
  }, [shareScopeFieldIds]);

  const handleClearShareScopeSelection = useCallback(() => {
    setShareSelectedFieldIds([]);
    setShareScopeError(null);
  }, []);

  const handleCreateShareSession = useCallback(async () => {
    if (!checklist?.id) {
      return;
    }

    if (shareScopeSelectedCount === 0) {
      const message = 'Selecione ao menos um campo para compartilhar.';
      setShareScopeError(message);
      showToast(message, { duration: 2200, type: 'error' });
      return;
    }

    if (shareScopeTotalFields === 0) {
      const message = 'Nenhum campo compatível com compartilhamento público foi encontrado neste checklist.';
      setShareScopeError(message);
      showToast(message, { duration: 2200, type: 'error' });
      return;
    }

    setShareError(null);
    setShareScopeError(null);
    setShareCreating(true);
    setClientControlActive(false);
    transitionShareFlowModal('share', 'create_share_session_start');
    setClientActivityMessage(null);
    setClientCompletionMessage(null);
    try {
      IdChecklistLogger.Main.info(`[CHECKLIST-EDITOR] Iniciando criação de sessão pública para checklist=${checklist.id}`);
      const scopeMode: ChecklistShareScopeMode = shareHasCustomScope ? 'custom_fields' : 'template_default';
      const allowedFieldIds = scopeMode === 'custom_fields' ? shareSelectedScopeFieldIdsOrdered : null;
      const created = await createChecklistShareLink({
        checklistId: checklist.id,
        ttlMinutes: SHARE_TTL_MINUTES,
        maxUpdates: SHARE_MAX_UPDATES,
        scopeMode,
        allowedFieldIds,
      });

      const baseSession: ChecklistShareSession = {
        shareLinkId: created.share_link_id,
        checklistId: created.checklist_id,
        status: created.status,
        expiresAt: created.expires_at,
        expiresInSeconds: getSecondsRemaining(created.expires_at, Date.now()),
        updatesCount: 0,
        maxUpdates: created.max_updates,
        lastActivityAt: null,
        revokedAt: null,
        completedAt: null,
        createdAt: null,
        updatedAt: null,
        url: created.url,
        token: created.token,
        ttlMinutes: created.ttl_minutes,
        scopeMode: created.scope_mode || scopeMode,
        allowedFieldIds:
          created.scope_mode === 'custom_fields'
            ? (created.allowed_field_ids && created.allowed_field_ids.length > 0
                ? created.allowed_field_ids
                : allowedFieldIds)
            : null,
      };

      setShareSession(baseSession);
      setShareScopeCollapsed(true);
      setClientCompletionMessage(null);

      try {
        const statusPayload = await getChecklistShareStatus(created.share_link_id);
        hydrateShareSessionFromStatus(statusPayload, baseSession);
      } catch (statusError) {
        IdChecklistLogger.Main.warn(
          `[CHECKLIST-EDITOR] Falha ao carregar status inicial da sessão de compartilhamento: ${toErrorMessage(statusError)}`
        );
      }

      IdChecklistLogger.Main.info(`[CHECKLIST-EDITOR] Sessão pública criada shareLinkId=${created.share_link_id}`);
      showToast('Link de compartilhamento gerado!', { duration: 1400, type: 'success' });
    } catch (error) {
      const message = toErrorMessage(error);
      setShareError(message);
      showToast(`Erro ao gerar link: ${message}`, { duration: 2200, type: 'error' });
      captureSentryExceptionWithScope(error, {
        level: 'error',
        context: {
          flow: 'checklist_share_modal',
          stage: 'create',
          checklistId: checklist.id,
        },
        tags: {
          flow: 'checklist_share_modal',
          stage: 'create',
        },
      });
    } finally {
      setShareCreating(false);
    }
  }, [
    checklist?.id,
    hydrateShareSessionFromStatus,
    shareHasCustomScope,
    shareScopeSelectedCount,
    shareSelectedScopeFieldIdsOrdered,
    shareScopeTotalFields,
    transitionShareFlowModal,
  ]);

  const handleRefreshShareStatus = useCallback(async (silent = false) => {
    if (!shareSession?.shareLinkId) {
      return;
    }

    if (!silent) {
      IdChecklistLogger.Main.info(
        `[CHECKLIST-EDITOR] Atualizando status do link público shareLinkId=${shareSession.shareLinkId}`
      );
    }

    if (!silent) {
      setShareRefreshing(true);
    }
    try {
      const payload = await getChecklistShareStatus(shareSession.shareLinkId);
      hydrateShareSessionFromStatus(payload);
      setShareError(null);
    } catch (error) {
      const message = toErrorMessage(error);
      if (!silent) {
        setShareError(message);
        showToast(`Erro ao atualizar status: ${message}`, { duration: 2000, type: 'error' });
      }
      captureSentryExceptionWithScope(error, {
        level: silent ? 'info' : 'warning',
        context: {
          flow: 'checklist_share_modal',
          stage: silent ? 'status_silent' : 'status',
          shareLinkId: shareSession.shareLinkId,
          silent,
        },
        tags: {
          flow: 'checklist_share_modal',
          stage: silent ? 'status_silent' : 'status',
          silent,
        },
        dedupeKey: silent
          ? `checklist_share_modal:status_silent:${shareSession.shareLinkId}`
          : `checklist_share_modal:status:${shareSession.shareLinkId}:${message}`,
        dedupeWindowMs: silent ? 60000 : 20000,
      });
    } finally {
      if (!silent) {
        setShareRefreshing(false);
      }
    }
  }, [hydrateShareSessionFromStatus, shareSession?.shareLinkId]);

  const handleRevokeShareSession = useCallback(async () => {
    if (!shareSession?.shareLinkId) {
      return;
    }

    setShareError(null);
    setShareRevoking(true);
    try {
      const payload = await revokeChecklistShareLink(shareSession.shareLinkId);
      setShareSession(prev => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          status: payload.share_link.status,
          revokedAt: payload.share_link.revoked_at,
          completedAt: payload.share_link.completed_at,
          updatesCount: payload.share_link.updates_count,
          maxUpdates: payload.share_link.max_updates,
        };
      });
      setClientControlActive(false);
      setClientActivityMessage(null);
      setClientCompletionMessage(null);
      transitionShareFlowModal('share', 'share_session_revoked');
      showToast('Acesso revogado com sucesso.', { duration: 1500, type: 'success' });
      IdChecklistLogger.Main.info(`[CHECKLIST-EDITOR] Link público revogado shareLinkId=${shareSession.shareLinkId}`);
    } catch (error) {
      const message = toErrorMessage(error);
      setShareError(message);
      showToast(`Erro ao revogar link: ${message}`, { duration: 2200, type: 'error' });
      captureSentryExceptionWithScope(error, {
        level: 'error',
        context: {
          flow: 'checklist_share_modal',
          stage: 'revoke',
          shareLinkId: shareSession.shareLinkId,
        },
        tags: {
          flow: 'checklist_share_modal',
          stage: 'revoke',
        },
      });
    } finally {
      setShareRevoking(false);
    }
  }, [shareSession?.shareLinkId, transitionShareFlowModal]);

  const handleRetakeChecklistControl = useCallback(async () => {
    const currentSession = shareSessionRef.current;
    if (!currentSession?.shareLinkId) {
      setClientControlActive(false);
      transitionShareFlowModal(null, 'retake_control_without_session');
      return;
    }

    setClientControlBusy(true);
    setShareError(null);
    try {
      IdChecklistLogger.Main.info(
        `[CHECKLIST-EDITOR] Retomando controle do checklist via revogação do link ${currentSession.shareLinkId}`
      );
      await revokeChecklistShareLink(currentSession.shareLinkId);

      setShareSession((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          status: 'revoked',
          revokedAt: new Date().toISOString(),
        };
      });

      setClientControlActive(false);
      setClientActivityMessage(null);
      setClientCompletionMessage(null);
      transitionShareFlowModal(null, 'retake_control_success');

      await refetch();
      showToast('Controle retomado. Checklist atualizado.', { duration: 1800, type: 'success' });
    } catch (error) {
      const message = toErrorMessage(error);
      setShareError(message);
      showToast(`Erro ao retomar controle: ${message}`, { duration: 2200, type: 'error' });
      captureSentryExceptionWithScope(error, {
        level: 'error',
        context: {
          flow: 'checklist_share_modal',
          stage: 'retake_control',
          shareLinkId: currentSession.shareLinkId,
        },
        tags: {
          flow: 'checklist_share_modal',
          stage: 'retake_control',
        },
      });
    } finally {
      setClientControlBusy(false);
    }
  }, [refetch, transitionShareFlowModal]);

  const handleOpenShareModal = useCallback(() => {
    if (!isOnline) {
      showToast('Conecte-se à internet para compartilhar este checklist.', {
        duration: 2200,
        type: 'error',
      });
      return;
    }
    IdChecklistLogger.Main.info(
      `[CHECKLIST-EDITOR] Abrindo modal de compartilhamento. hasSession=${!!shareSession}, status=${shareSession?.status ?? 'none'}`
    );
    setShareError(null);
    setShareScopeError(null);
    setShareScopeCollapsed(!!shareSession);

    if (clientControlActive) {
      transitionShareFlowModal('client_control', 'open_share_action_while_client_active');
    } else {
      transitionShareFlowModal('share', 'open_share_action');
    }

    const canReuseCurrentSession =
      !!shareSession &&
      shareSession.status === 'active' &&
      getSecondsRemaining(shareSession.expiresAt, Date.now()) > 0;

    if (canReuseCurrentSession) {
      void handleRefreshShareStatus(true);
      return;
    }

    if (shareSession) {
      // Sessão já existe (expirada/revogada/concluída): abre modal em modo leitura.
      void handleRefreshShareStatus(true);
      return;
    }
  }, [
    clientControlActive,
    handleRefreshShareStatus,
    isOnline,
    shareSession,
    transitionShareFlowModal,
  ]);

  const handleCloseShareModal = useCallback(() => {
    IdChecklistLogger.Main.info('[CHECKLIST-EDITOR] Fechando modal de compartilhamento.');
    transitionShareFlowModal(null, 'share_modal_close');
    setShareScopeError(null);
    setShareCopyFeedbackVisible(false);
  }, [transitionShareFlowModal]);

  const handleOpenTransferModal = useCallback(async () => {
    if (!checklist?.id || !canOpenTransfer) {
      showToast('A transferência não está disponível para este checklist.', { duration: 2000, type: 'error' });
      return;
    }

    setTransferBusy(true);
    setTransferError(null);
    setTransferTarget(null);
    try {
      const candidates = await checklistTransferService.getCandidates(checklist, {
        id: currentUserId,
        empresa_id: currentEmpresaId,
        user_type: currentUserType,
      });
      setTransferCandidates(candidates);
      setTransferModalKind('list');
      IdChecklistLogger.Main.info(
        `[CHECKLIST-EDITOR] Modal de transferência aberto checklist=${checklist.id.slice(-6)} candidatos=${candidates.length}`
      );
    } catch (error) {
      const message = error instanceof ChecklistTransferError
        ? error.message
        : 'Não foi possível carregar os responsáveis elegíveis. Sincronize e tente novamente.';
      setTransferCandidates([]);
      if (!(error instanceof ChecklistTransferError && error.code === 'offline')) {
        setTransferModalKind('list');
      }
      setTransferError(message);
      showToast(message, { duration: 2200, type: 'error' });
    } finally {
      setTransferBusy(false);
    }
  }, [canOpenTransfer, checklist, currentEmpresaId, currentUserId, currentUserType]);

  const handleSelectTransferTarget = useCallback((candidate: ChecklistAssignmentCandidate) => {
    setTransferTarget(candidate);
    setTransferError(null);
    setTransferModalKind('confirm');
  }, []);

  const handleTransferChecklist = useCallback(async () => {
    if (!checklist || !transferTarget?.id || !canOpenTransfer) return;

    setTransferBusy(true);
    setTransferError(null);
    try {
      const result = await checklistTransferService.transfer({
        checklist: checklist as unknown as ChecklistTransferChecklist,
        actor: {
          id: currentUserId,
          empresa_id: currentEmpresaId,
          user_type: currentUserType,
        },
        targetUserId: transferTarget.id,
      });
      setTransferModalKind(null);
      setTransferTarget(null);
      showToast(
        result.revokedLinksCount > 0
          ? `Checklist transferido para ${transferTarget.name?.trim() || 'o responsável'}. O link público foi revogado.`
          : `Checklist transferido para ${transferTarget.name?.trim() || 'o responsável'}.`,
        { duration: 1800, type: 'success' }
      );
      await refetch();
    } catch (error) {
      const message = toErrorMessage(error);
      setTransferError(message);
      showToast(message, { duration: 2400, type: 'error' });
      IdChecklistLogger.Main.warn(
        `[CHECKLIST-EDITOR] Falha na transferência checklist=${checklist.id.slice(-6)}`
      );
    } finally {
      setTransferBusy(false);
    }
  }, [canOpenTransfer, checklist, currentEmpresaId, currentUserId, currentUserType, refetch, transferTarget]);

  const handleCloseTransferModal = useCallback(() => {
    if (transferBusy) return;
    setTransferModalKind(null);
    setTransferTarget(null);
    setTransferError(null);
  }, [transferBusy]);

  const triggerShareCopyFeedback = useCallback(() => {
    setShareCopyFeedbackVisible(true);
    if (shareCopyFeedbackTimerRef.current) {
      clearTimeout(shareCopyFeedbackTimerRef.current);
    }
    shareCopyFeedbackTimerRef.current = setTimeout(() => {
      setShareCopyFeedbackVisible(false);
      shareCopyFeedbackTimerRef.current = null;
    }, 1800);
  }, []);

  const handleCloseClientCompletionModal = useCallback(() => {
    IdChecklistLogger.Main.info('[CHECKLIST-EDITOR] Fechando popup de conclusão do cliente.');
    setClientCompletionMessage(null);
    setClientControlActive(false);
    setClientActivityMessage(null);
    transitionShareFlowModal(null, 'client_completed_close');
    // Reforço de consistência ao voltar para edição.
    void refetch();
    showToast('Checklist liberado para edição.', { duration: 1600, type: 'success' });
  }, [refetch, transitionShareFlowModal]);

  const handleCopyShareUrl = useCallback(async () => {
    const currentSession = shareSession;
    if (!currentSession?.url) {
      return;
    }

    IdChecklistLogger.Main.info(
      `[CHECKLIST-EDITOR] Copiando link público shareLinkId=${currentSession.shareLinkId}`
    );

    const errors: string[] = [];

    if (Platform.OS === 'web') {
      const maybeNavigator =
        typeof globalThis !== 'undefined'
          ? (globalThis as unknown as { navigator?: { clipboard?: { writeText?: (value: string) => Promise<void> } } })
              .navigator
          : undefined;
      const writeText = maybeNavigator?.clipboard?.writeText;

      if (typeof writeText === 'function') {
        try {
          await writeText(currentSession.url);
          setShareError(null);
          triggerShareCopyFeedback();
          return;
        } catch (error) {
          const message = toErrorMessage(error);
          errors.push(`navigator.clipboard: ${message}`);
          IdChecklistLogger.Main.warn(
            `[CHECKLIST-EDITOR] Falha ao copiar via navigator.clipboard: ${message}`
          );
        }
      }
    }

    try {
      const clipboardResult = Clipboard.setString(currentSession.url) as unknown;
      if (clipboardResult === false) {
        throw new Error('Clipboard.setString retornou false');
      }

      setShareError(null);
      triggerShareCopyFeedback();
      return;
    } catch (error) {
      errors.push(`clipboard.setString: ${toErrorMessage(error)}`);
    }

    const combinedErrorMessage = errors.length > 0 ? errors.join(' | ') : 'Falha ao copiar link';
    const copyError = new Error(combinedErrorMessage);
    setShareError(combinedErrorMessage);
    captureSentryExceptionWithScope(copyError, {
      level: 'warning',
      context: {
        flow: 'checklist_share_modal',
        stage: 'copy_link',
        hasUrl: !!currentSession.url,
        attempts: errors,
      },
      tags: {
        flow: 'checklist_share_modal',
        stage: 'copy_link',
      },
      dedupeKey: `checklist_share_modal:copy_link:${currentSession.shareLinkId}`,
      dedupeWindowMs: 30000,
    });
    showMessage('Link público', `Não foi possível copiar automaticamente.\n\nLink:\n${currentSession.url}`);
  }, [shareSession, showMessage, triggerShareCopyFeedback]);

  const handleShareViaWhatsApp = useCallback(async () => {
    const currentSession = shareSession;
    if (!currentSession?.url) {
      return;
    }

    const encodedLink = encodeURIComponent(currentSession.url);
    const nativeWhatsAppUrl = `whatsapp://send?text=${encodedLink}`;
    const webWhatsAppUrl = buildWhatsAppShareUrl(currentSession.url);

    try {
      IdChecklistLogger.Main.info(
        `[CHECKLIST-EDITOR] Compartilhando link no WhatsApp shareLinkId=${currentSession.shareLinkId}`
      );

      if (Platform.OS !== 'web') {
        try {
          await Linking.openURL(nativeWhatsAppUrl);
          setShareError(null);
          showToast('Abrindo WhatsApp...', { duration: 1500, type: 'success' });
          return;
        } catch (nativeError) {
          IdChecklistLogger.Main.warn(
            `[CHECKLIST-EDITOR] Falha ao abrir esquema nativo do WhatsApp: ${toErrorMessage(nativeError)}`
          );
        }
      }

      await Linking.openURL(webWhatsAppUrl);
      setShareError(null);
      showToast('Abrindo compartilhamento no WhatsApp...', { duration: 1600, type: 'success' });
    } catch (error) {
      const message = toErrorMessage(error);
      setShareError(message);
      captureSentryExceptionWithScope(error, {
        level: 'warning',
        context: {
          flow: 'checklist_share_modal',
          stage: 'share_whatsapp',
          hasUrl: !!currentSession.url,
        },
        tags: {
          flow: 'checklist_share_modal',
          stage: 'share_whatsapp',
        },
        dedupeKey: `checklist_share_modal:share_whatsapp:${currentSession.shareLinkId}`,
        dedupeWindowMs: 30000,
      });
      showMessage(
        'WhatsApp',
        `Não foi possível abrir o WhatsApp automaticamente.\n\nCopie e envie este link:\n${currentSession.url}`
      );
    }
  }, [shareSession, showMessage]);

  useEffect(() => {
    if (!isShareFlowModalVisible || !shareSession?.expiresAt || shareSession.status !== 'active') {
      return;
    }

    // Atualiza o contador no modal para refletir expiração em tempo real.
    const tickTimer = setInterval(() => {
      setShareNowTick(Date.now());
    }, 1000);

    return () => clearInterval(tickTimer);
  }, [isShareFlowModalVisible, shareSession?.expiresAt, shareSession?.status]);

  useEffect(() => {
    if (!shareSession?.shareLinkId || shareSession.status !== 'active') {
      return;
    }

    const intervalMs = isShareFlowModalVisible ? 10000 : 12000;

    // Mantém status e última atividade sincronizados enquanto a sessão pública estiver ativa.
    const statusPollTimer = setInterval(() => {
      void handleRefreshShareStatus(true);
    }, intervalMs);

    return () => clearInterval(statusPollTimer);
  }, [handleRefreshShareStatus, isShareFlowModalVisible, shareSession?.shareLinkId, shareSession?.status]);

  // Registra somente a quantidade de campos; o conteúdo do checklist é sensível.
  useEffect(() => {
    IdChecklistLogger.Main.info(
      `[CHECKLIST-EDITOR] values atualizados campos=${Object.keys(values || {}).length}`
    );
  }, [JSON.stringify(values)]);

  useEffect(() => {
    if (!values || checklist?.id !== id) return;
    const readVersion = historicalRead?.valuesReadVersion ?? valuesReadVersion;
    const merged = mergeChecklistDraftRefresh(draft.peek(), lastSyncedDraftRef.current, values, unobservedConfirmedRef.current,
      readVersion > confirmedWriteVersionRef.current);
    lastSyncedDraftRef.current = merged.confirmed;
    draft.set(merged.draft);
  }, [JSON.stringify(values), historicalRead?.valuesReadVersion, valuesReadVersion, checklist?.id, id]);

  useEffect(() => { if (pending || hasUnsavedMarks) setSaved(false); }, [pending, hasUnsavedMarks]);

  // Loga mudanças de pending/spinner
  useEffect(() => {
    const now = Date.now();
    const base = typingStartRef.current;
    const rel = base ? (now - base) : 0;
    // Se pending ficou true, marca início do ciclo
    if (pending && !lastPendingRef.current) {
      typingStartRef.current = now;
      IdChecklistLogger.Main.info(`[CHECKLIST-EDITOR] pending TRUE (início do ciclo) em +0ms (absoluto ${now})`);
    } else {
      IdChecklistLogger.Main.info(`[CHECKLIST-EDITOR] pending mudou para ${pending} em +${rel}ms (absoluto ${now})`);
    }
    lastPendingRef.current = pending;
  }, [pending]);

  // Loga mudanças de saved/checkmark
  useEffect(() => {
    const now = Date.now();
    const base = typingStartRef.current;
    const rel = base ? (now - base) : 0;
    IdChecklistLogger.Main.info(`[CHECKLIST-EDITOR] saved mudou para ${saved} em +${rel}ms (absoluto ${now})`);
  }, [saved]);

  // Loga status do checklist sempre que mudar
  useEffect(() => {
    IdChecklistLogger.Main.info(`[CHECKLIST-EDITOR] status atual do checklist: ${checklist?.status}`);
  }, [checklist?.status]);

  // Loga montagem dos botões de ação
  useEffect(() => {
    if (!checklist) return;
    IdChecklistLogger.Main.info('[CHECKLIST-EDITOR] Renderizando botões. in_progress? ' + (checklist.status === 'in_progress') + ' | done? ' + (checklist.status === 'done'));
  }, [checklist?.status, loadingAction, pending]);

  // Recarrega dados sempre que a tela ganhar foco (mobile mantém stack montado)
  useFocusEffect(
    React.useCallback(() => {
      if (!initialFocusHandledRef.current) {
        initialFocusHandledRef.current = true;
        return;
      }
      //IdChecklistLogger.Main.info('[CHECKLIST-EDITOR] foco – recarregando checklist', { id });
      void refetch();
    }, [refetch])
  );

  const areDraftsEqual = useCallback((a: Record<string, string> = {}, b: Record<string, string> = {}) => {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      if (a[key] !== b[key]) {
        return false;
      }
    }
    return true;
  }, []);

  const computeValuesForPdf = useCallback(() => {
    const baseValues = values ? { ...values } : {};
    const draftSnapshot = draft.peek();
    return { ...baseValues, ...draftSnapshot };
  }, [draft, values]);

  type GeneratePdfOverrides = {
    checklistOverride?: Checklist;
    templateOverride?: typeof template;
    valuesOverride?: Record<string, string>;
  };

  const handleGeneratePdf = useCallback(async (overrides?: GeneratePdfOverrides) => {
    const checklistPayload = overrides?.checklistOverride ?? checklist;
    const templatePayload = overrides?.templateOverride ?? template;
    if (!checklistPayload || !templatePayload) return;
    setPdfBusy(true);
    setLoadingAction('pdf');
    try {
      if (!formRef.current) throw new Error('Aguarde o formulário terminar de carregar.');
      await formRef.current.flushPending();
      const mergedValues = overrides?.valuesOverride ?? computeValuesForPdf();
      let generatePdf: any;
      if (Platform.OS === 'web') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        ({ generateChecklistPdfWeb: generatePdf } = require('../../../services/checklistPdfService.web'));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        ({ generateChecklistPdfNative: generatePdf } = require('../../../services/checklistPdfService.native'));
      }
      if (!generatePdf) throw new Error('Função de geração de PDF não encontrada');
      const cache = await generatePdf({
        checklist: checklistPayload,
        template: templatePayload,
        values: mergedValues,
        ...(Platform.OS === 'ios' && {
          onBeforePresentActions: async () => {
            setLoadingAction(null);
            setPdfBusy(false);
            showToast('PDF pronto para salvar ou compartilhar.', {
              duration: 1800,
              type: 'info',
            });
            await new Promise((resolve) => setTimeout(resolve, 400));
          },
        }),
      });
      if (Platform.OS !== 'web') setPdfCacheUri(cache);
      if (Platform.OS !== 'ios') {
        showToast('PDF gerado com sucesso!', { duration: 1200, type: 'success' });
      }
    } catch (err: any) {
      const message = err?.message || String(err);
      if (message === CHECKLIST_UNSAVED_MARKS_MESSAGE) setSaveError(message);
      showToast(message === CHECKLIST_UNSAVED_MARKS_MESSAGE ? message : `Erro ao gerar PDF: ${message}`, { duration: 2500, type: 'error' });
    } finally {
      setLoadingAction(null);
      setPdfBusy(false);
    }
  }, [checklist, computeValuesForPdf, navigation, template]);

  const askGeneratePdf = useCallback((): Promise<'generate' | 'skip'> => {
    return new Promise(resolve => {
      const question = 'Deseja gerar o PDF agora? Você pode gerar a qualquer momento na aba de checklists concluídos.';
      const webButtons = [
        { text: 'Depois', style: 'cancel' as const, onPress: () => resolve('skip') },
        { text: 'Gerar agora', onPress: () => resolve('generate') },
      ];
      const nativeButtons = [
        { text: 'Depois', style: 'cancel' as const, onPress: () => resolve('skip') },
        { text: 'Gerar agora', onPress: () => resolve('generate') },
      ];
      if (Platform.OS !== 'android') {
        showAlert('Checklist concluído', question, webButtons);
      } else {
        Alert.alert(
          'Checklist concluído',
          question,
          nativeButtons,
          { cancelable: true, onDismiss: () => resolve('skip') }
        );
      }
    });
  }, [showAlert]);

  // Handler para concluir checklist
  const handleComplete = useCallback(async () => {
    IdChecklistLogger.Main.info(
      `[CHECKLIST-EDITOR] handleComplete checklist=${checklist?.id?.slice(-6) || 'n/a'} campos=${Object.keys(draft.peek() || {}).length}`
    );
    if (!checklist) return;
    setLoadingAction('complete');
    try {
      const now = Date.now();
      // Compartilha o autosave em andamento e confirma também as edições mais recentes.
      if (!formRef.current) throw new Error('Aguarde o formulário terminar de carregar.');
      await formRef.current.flushPending();

      const partnerBaseline = readPartnerChecklistBaseline(currentEmpresaId || '', currentUserId || '', checklist.id);
      if (partner.state?.touched || partnerBaseline?.touched) {
        if (!isOnline || !partnerBaseline) throw new Error('Conecte-se e atualize este checklist antes de concluir.');
        const { syncAll } = await import('@/services/syncService');
        await syncAll();
        if (await hasPartnerChecklistPending(checklist.id)) throw new Error('Aguarde a sincronização das alterações antes de concluir.');
        try {
          await callPartnerOwner(partnerScope, 'owner_complete', {
            expectedRevision: partnerBaseline.revision, requestId: newPartnerRequestId(),
          });
        } catch (e) { throw new Error(partnerOwnerErrorMessage(e)); }
      }
      // 2) Agora marca o checklist como finalizado (status = 'done')
      //    não enviar fields_json, pois não existe essa coluna
      await databaseService.updateItem(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
        checklist.id,
        {
          status: 'done',
          data_conclusao: now,
          updated_at: now
        },
        Boolean(partner.state?.touched || partnerBaseline?.touched) // A conclusão protegida já foi confirmada pelo servidor.
      );

      const updatedChecklist = { ...checklist, status: 'done' as const, data_conclusao: now };
      const mergedValues = computeValuesForPdf();

      showToast('Checklist concluído!', { duration: 1200, type: 'success' });
      setLoadingAction(null);
      const decision = await askGeneratePdf();
      if (decision === 'generate') {
        await handleGeneratePdf({
          checklistOverride: updatedChecklist,
          templateOverride: template,
          valuesOverride: mergedValues,
        });
      }
      navigateBack();

    } catch (err: any) {
      const message = err?.message || String(err);
      if (message === CHECKLIST_UNSAVED_MARKS_MESSAGE) setSaveError(message);
      showToast(message === CHECKLIST_UNSAVED_MARKS_MESSAGE ? message : `Erro ao concluir checklist: ${message}`, { duration: 2500, type: 'error' });
    } finally {
      setLoadingAction(null);
    }
  }, [areDraftsEqual, askGeneratePdf, checklist, computeValuesForPdf, handleGeneratePdf, router, template, partner.state, currentEmpresaId, currentUserId, isOnline]);

  const handleReopenChecklist = useCallback(() => {
    if (!checklist || !canReopenChecklist) return;
    showMessage(
      'Reabrir checklist',
      'Deseja mudar este checklist para "Em Andamento"?',
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Reabrir',
          onPress: async () => {
            setLoadingAction('reopen');
            try {
              const now = Date.now();
              const partnerBaseline = readPartnerChecklistBaseline(currentEmpresaId || '', currentUserId || '', checklist.id);
              if (partner.state?.touched || partnerBaseline?.touched) {
                if (!isOnline || !partnerBaseline) throw new Error('Conecte-se e atualize este checklist antes de reabrir.');
                if (await hasPartnerChecklistPending(checklist.id)) throw new Error('Aguarde a sincronização antes de reabrir.');
                try {
                  await callPartnerOwner(partnerScope, 'owner_reopen', { expectedRevision: partnerBaseline.revision, requestId: newPartnerRequestId() });
                } catch (e) { throw new Error(partnerOwnerErrorMessage(e)); }
              }
              await databaseService.updateItem(
                APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
                checklist.id,
                {
                  status: 'in_progress',
                  data_conclusao: null,
                  updated_at: now,
                },
                Boolean(partner.state?.touched || partnerBaseline?.touched)
              );
              await partner.refresh();
              showToast('Checklist reaberto com sucesso!', { duration: 1500, type: 'success' });
              refetch();
            } catch (err: any) {
              showToast('Erro ao reabrir checklist: ' + (err?.message || err), { duration: 2000, type: 'error' });
            } finally {
              setLoadingAction(null);
            }
          }
        }
      ]
    );
  }, [canReopenChecklist, checklist, refetch, showMessage, partner.state, currentEmpresaId, currentUserId, isOnline]);

  // Handler para deletar checklist
  const handleDelete = () => {
    if (!checklist || !canDeleteChecklist || loadingAction === 'delete') return;
    showMessage(
      'Deletar Checklist',
      'Tem certeza que deseja deletar este checklist? Esta ação não pode ser desfeita.',
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Deletar', style: 'destructive', onPress: async () => {
            setLoadingAction('delete');
            try {
              // Utiliza o helper centralizado para deleção
              await ChecklistActionsHelper.deleteMany([checklist.id]);
              // A exclusão confirmada não deve disparar outro salvamento do formulário removido.
              bypassRemovalRef.current = true;
              navigateBack();
            } catch (err: any) {
              showToast('Erro ao deletar: ' + (err?.message || err), { duration: 2000, type: 'error' });
            } finally {
              setLoadingAction(null);
            }
          }
        }
      ]
    );
  };

  const handlePendingChange = useCallback((nextPending: boolean) => {
    setPending(nextPending);
    if (nextPending) setSaved(false);
  }, []);

  const handleDraftFlushed = useCallback((changes: Record<string, string>) => {
    const { cleaned } = sanitizeDraftForPersistence(changes);
    lastSyncedDraftRef.current = { ...lastSyncedDraftRef.current, ...cleaned };
    Object.assign(unobservedConfirmedRef.current, cleaned);
    confirmedWriteVersionRef.current = markValuesWriteConfirmed();
    setSaved(!hasUnsavedMarks && areDraftsEqual(lastSyncedDraftRef.current, sanitizeDraftForPersistence(draft.peek()).cleaned));
  }, [sanitizeDraftForPersistence, hasUnsavedMarks, areDraftsEqual, draft, markValuesWriteConfirmed]);

  const preventLeaving = hasUnsavedMarks || (checklist?.status === 'in_progress' && canEditChecklistRole && historicalRead?.readOnly !== true && (
    pending || !areDraftsEqual(lastSyncedDraftRef.current, sanitizeDraftForPersistence(draft.get()).cleaned)
  ));
  const { saving: savingBeforeLeave, leave } = useChecklistLeaveGuard({
    prevent: Boolean(preventLeaving),
    bypass: () => bypassRemovalRef.current,
    getScope: () => {
      if (!leaveFocusRef.current.focused) return null;
      const liveIdentity = `${String(id)}:${globalState.user.user.peek()?.id || null}:${globalState.user.profile.peek()?.empresa_id || null}`;
      if (liveIdentity !== leaveIdentityRef.current) return null;
      return `${leaveIdentityRef.current}:${leaveFocusRef.current.generation}`;
    },
    flush: async () => {
      if (!formRef.current) throw new Error('Aguarde o formulário terminar de carregar.');
      await formRef.current.flushPending();
    },
    onError: error => {
      IdChecklistLogger.Main.error(`[CHECKLIST-EDITOR] saída bloqueada checklist=${checklist?.id}:`, error);
      setSaveError(error instanceof Error && error.message === CHECKLIST_UNSAVED_MARKS_MESSAGE
        ? CHECKLIST_UNSAVED_MARKS_MESSAGE
        : 'Não foi possível salvar as últimas alterações. Elas continuam nesta tela. Aguarde e tente voltar novamente.');
    },
  });

  const handleBack = useCallback(() => { void leave(navigateBack); return true; }, [leave, navigateBack]);

  const handleHardwareBackPress = useCallback(() => {
    if (editorMenu) { setEditorMenu(null); return true; }
    const now = Date.now();
    const photoBackGuardState = photoBackGuardStateRef.current;
    const shouldConsumePhotoBack =
      Platform.OS === 'android' && now < photoBackGuardUntilRef.current;

    if (shouldConsumePhotoBack) {
      IdChecklistLogger.Main.info(
        `[CHECKLIST-EDITOR] hardwareBackPress consumido durante fluxo de foto campo=${photoBackGuardState?.fieldId || 'n/a'} restanteMs=${Math.max(0, photoBackGuardUntilRef.current - now)}`
      );
      return true;
    }

    return handleBack();
  }, [handleBack, editorMenu]);

  // Interceptar "voltar" físico/gesto
  useFocusEffect(useCallback(() => {
    const subscription = BackHandler.addEventListener('hardwareBackPress', handleHardwareBackPress);
    return () => subscription.remove();
  }, [handleHardwareBackPress]));

  // Limpeza de arquivo PDF em cache ao sair da tela
  useEffect(() => {
    // Se não há cache, nada a fazer
    if (!pdfCacheUri) return;

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const FileSystem = require('expo-file-system/legacy');
      FileSystem.deleteAsync(pdfCacheUri, { idempotent: true }).catch(() => {});
    };
  }, [pdfCacheUri]);

  // Uma troca de checklist ou sessão não pode reutilizar o rascunho anterior.
  useEffect(() => {
    IdChecklistLogger.Main.info(`[CHECKLIST-EDITOR] useEffect - reset draft para id: ${id}`);
    draft.set({});
    lastSyncedDraftRef.current = {};
    unobservedConfirmedRef.current = {};
    confirmedWriteVersionRef.current = 0;
    setSaveError(null);
    setPending(false);
    setSaved(false);
    bypassRemovalRef.current = false;

  }, [editorIdentity]);

  // A sincronização do link não pode desmontar o modal e perder a URL recém-gerada.
  if (loading && (checklist?.id !== id || (!pending && !partnerModalVisible))) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} style={{ marginTop: 48 }} />
        <Text style={styles.placeholderText}>Carregando checklist...</Text>
      </View>
    );
  }

  if (error && !pending) {
    return (
      <View style={styles.container}>
        <Text style={styles.placeholderText}>Erro: {error}</Text>
      </View>
    );
  }

  if (!checklist) {
    return (
      <View style={styles.container}>
        <Text style={styles.placeholderText}>Checklist não encontrado.</Text>
      </View>
    );
  }

  const canViewChecklist = canViewChecklistForRole({
    userType: currentUserType,
    checklistUsuarioId: checklist.usuario_id ?? null,
    checklistEmpresaId: checklist.empresa_id ?? null,
    currentUserId,
    currentEmpresaId,
  });
  const canEditChecklist = canEditChecklistRole && checklist.status === 'in_progress' && historicalRead?.readOnly !== true && !partner.blocked;

  if (!canViewChecklist) {
    return (
      <View style={styles.container}>
        <Text style={styles.placeholderText}>Voce nao tem permissao para visualizar este checklist.</Text>
        <TouchableOpacity style={styles.retryButton} onPress={() => router.back()}>
          <Text style={styles.retryButtonText}>Voltar</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const sequentialHasValue =
    (checklist.sequencial_codigo && checklist.sequencial_codigo.trim().length > 0) ||
    (typeof checklist.sequencial_numero === 'number' && Number.isFinite(checklist.sequencial_numero));
  const sequentialText = sequentialHasValue
    ? (checklist.sequencial_codigo?.trim() || `Checklist #${checklist.sequencial_numero}`)
    : 'Aguardando numeração';
  const completedFieldCount = countChecklistFilledFields(template, draft.get());
  const profile = globalState.user.profile.get();
  const showDashboard = hasPremiumAccess(subscriptionSnapshot, profile) && canAccessChecklistDashboard(currentUserType);
  const showDeveloper = hasDeveloperBypass(currentUserType);
  const runEditorAction = (action: () => void | Promise<void>) => {
    setEditorMenu(null);
    // O menu é uma camada de View; aguardar o render antes de apresentar os modais existentes.
    requestAnimationFrame(() => { void action(); });
  };
  const menuAction = (label: string, icon: React.ComponentProps<typeof Ionicons>['name'], action: () => void | Promise<void>, disabled = false, destructive = false) => (
    <TouchableOpacity key={label} accessibilityRole="button" accessibilityLabel={label} disabled={disabled}
      onPress={() => runEditorAction(action)} style={[styles.editorMenuRow, disabled && styles.actionButtonDisabled]}>
      <Ionicons name={icon} size={22} color={destructive ? theme.colors.util.error : theme.colors.primary.default} />
      <Text style={styles.editorMenuLabel}>{label}</Text>
    </TouchableOpacity>
  );
  const navigationItem = (label: string, icon: React.ComponentProps<typeof Ionicons>['name'], href: string) => (
    <AppNavigationLink key={label} href={href}><TouchableOpacity accessibilityRole="button" accessibilityLabel={`Abrir ${label}`}
      onPress={() => setEditorMenu(null)} style={styles.editorMenuRow}>
      <Ionicons name={icon} size={22} color={theme.colors.primary.default} /><Text style={styles.editorMenuLabel}>{label}</Text>
    </TouchableOpacity></AppNavigationLink>
  );
  const shareEffectiveStatus: ChecklistShareLinkStatus | null =
    shareSession
      ? (shareSession.status === 'active' && shareSecondsRemaining <= 0 ? 'expired' : shareSession.status)
      : null;
  const shareStatusLabel = shareEffectiveStatus ? getShareStatusLabel(shareEffectiveStatus) : 'Sem sessão';
  const isShareSessionActive = shareEffectiveStatus === 'active';
  const canCreateShareSession = !shareCreating && shareScopeSelectedCount > 0 && shareScopeTotalFields > 0;
  const canCollapseShareScope = !!shareSession;
  const shareSessionScopeMode = shareSession?.scopeMode || 'template_default';
  const shareSessionAllowedFieldsSet =
    shareSessionScopeMode === 'custom_fields' && Array.isArray(shareSession?.allowedFieldIds)
      ? new Set(shareSession.allowedFieldIds)
      : null;
  const shareSessionScopeCount =
    shareSessionScopeMode === 'custom_fields' && shareSessionAllowedFieldsSet
      ? shareScopeFields.filter(field => shareSessionAllowedFieldsSet.has(field.id)).length
      : shareScopeTotalFields;
  const shareTerminalDescription =
    shareEffectiveStatus === 'completed'
      ? 'O cliente concluiu as alterações deste checklist. Gere um novo link apenas se precisar de uma nova rodada.'
      : shareEffectiveStatus === 'revoked'
      ? 'Este link foi revogado. Para continuar com preenchimento público, gere um novo link.'
      : shareEffectiveStatus === 'expired'
      ? 'Este link expirou. Gere um novo link para permitir novo acesso público.'
      : 'Sessão não está ativa para compartilhamento.';
  const shareStatusBadgeStyle =
    shareEffectiveStatus === 'active'
      ? styles.shareStatusBadgeActive
      : shareEffectiveStatus === 'revoked'
      ? styles.shareStatusBadgeRevoked
      : shareEffectiveStatus === 'expired'
      ? styles.shareStatusBadgeExpired
      : styles.shareStatusBadgeCompleted;
  const isShareStatusCompact = windowWidth <= 430;
  return (
    <View ref={actionsTipHostRef} collapsable={false} style={styles.container}>
      <ChecklistActionsTip userId={currentUserId} ready={!editorMenu && !isShareFlowModalVisible && !transferModalKind}
        anchorRef={actionsAnchorRef} hostRef={actionsTipHostRef} />
      <View style={[styles.editorHeader, { paddingTop: topBarPaddingTop }]}>
        <View style={styles.editorTopRow}>
          <TouchableOpacity style={styles.editorBack} onPress={handleBack} accessibilityRole="button" accessibilityLabel="Voltar">
            <Ionicons name="arrow-back" size={25} color={theme.colors.primary.default} />
          </TouchableOpacity>
          <View style={styles.editorTitleArea}>
            <Text style={styles.editorTitle}>Nº checklist: {sequentialText}</Text>
          </View>
          <View ref={actionsAnchorRef} collapsable={false}>
            <TouchableOpacity style={styles.editorTool} onPress={() => { Keyboard.dismiss(); setEditorMenu(previous => previous === 'actions' ? null : 'actions'); }}
              accessibilityRole="button" accessibilityLabel="Ações do checklist" accessibilityState={{ expanded: editorMenu === 'actions' }}>
              <Ionicons name="ellipsis-horizontal" size={22} color={theme.colors.primary.default} /><Text style={styles.editorToolText}>Ações</Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity style={styles.editorTool} onPress={() => { Keyboard.dismiss(); setEditorMenu(previous => previous === 'app' ? null : 'app'); }}
            accessibilityRole="button" accessibilityLabel="Menu do app" accessibilityState={{ expanded: editorMenu === 'app' }}>
            <Ionicons name="menu-outline" size={22} color={theme.colors.primary.default} /><Text style={styles.editorToolText}>Menu app</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.editorStatusRow}>
          <View accessible accessibilityRole="text" accessibilityLabel={`Status do checklist: ${checklist.status === 'done' ? 'Concluído' : 'Em andamento'}`}
            style={[styles.editorStatusBadge, checklist.status === 'done' && styles.editorStatusDone]}>
            <Ionicons name={checklist.status === 'done' ? 'checkmark-circle' : 'time-outline'} size={14} color={checklist.status === 'done' ? '#15803D' : '#92400E'} />
            <Text style={[styles.editorStatusText, checklist.status === 'done' && styles.editorStatusDoneText]}>{checklist.status === 'done' ? 'Concluído' : 'Em andamento'}</Text>
          </View>
          <View style={styles.editorMetadata}>
            <View style={styles.editorLocalState}>
              {pending ? <ActivityIndicator size="small" color={theme.colors.util.warning} /> : saved ? <Ionicons name="checkmark-circle" size={12} color={theme.colors.util.success} /> : null}
              <Text style={styles.editorStateText}>{hasUnsavedMarks ? 'Marcação não salva' : pending ? 'Salvando localmente' : saved ? 'Salvo localmente' : 'Salvamento automático'}</Text>
            </View>
            <Text style={styles.editorResponsible}>Responsável: {transferOwnerName}</Text>
          </View>
        </View>
      </View>
      {partner.blocked && <View style={{ padding: 12, backgroundColor: '#fff4d6' }}>
        <Text style={{ color: '#775515' }}>{partner.state?.active
          ? 'O parceiro está preenchendo. Para retomar, use Ações → Parceiro preencher por link.'
          : 'O checklist recebeu alterações do parceiro. Suas pendências foram preservadas. Se a sincronização indicar conflito, contate o suporte para conferir as duas versões.'}</Text>
      </View>}
      {clientControlActive && (
        <TouchableOpacity
          style={styles.clientControlBanner}
          onPress={() => transitionShareFlowModal('client_control', 'client_control_banner_press')}
          accessibilityRole="button"
          accessibilityLabel="Abrir painel de controle do checklist compartilhado"
        >
          <Ionicons name="shield-checkmark-outline" size={18} color={theme.colors.text.primary} />
          <View style={styles.clientControlBannerContent}>
            <Text style={styles.clientControlBannerTitle}>Cliente está preenchendo este checklist</Text>
            <Text style={styles.clientControlBannerSubtitle}>
              Toque para retomar o controle e atualizar os dados.
            </Text>
          </View>
        </TouchableOpacity>
      )}
      <View
        style={[
          styles.formContainer,
          clientControlActive && styles.formContainerLocked,
        ]}
        pointerEvents={clientControlActive ? 'none' : 'auto'}
      >
        {saveError && <Text accessibilityRole="alert" style={{ color: '#b42318', padding: 12 }}>{saveError}</Text>}
        <ChecklistFieldsForm
          ref={formRef}
          key={`${checklist.id}:${currentUserId}:${currentEmpresaId}`}                 // Cada checklist e identidade mantém seu próprio estado de edição.
          checklistId={checklist.id}
          empresaId={checklist.empresa_id}
          template={template}
          observationsEnabled={observationsEnabled}
          editable={canEditChecklist && !clientControlActive && loadingAction !== 'complete' && !savingBeforeLeave}
          draft={draft}
          onPendingChange={handlePendingChange}
          onUnsavedMarkChange={setHasUnsavedMarks}
          onMarkInteraction={handleMarkInteraction}
          onDraftFlushed={handleDraftFlushed}
          onSaveError={setSaveError}
          onGroupExpansionChange={setGroupExpansion}
          onPhotoFlowStateChange={handlePhotoFlowStateChange}
        />
      </View>
      <View style={[styles.editorFooter, { paddingBottom: Math.max(10, safeAreaInsets.bottom) }]}>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel="Recolher"
          accessibilityState={{ disabled: groupExpansion.allCollapsed || groupExpansion.captureBusy || clientControlActive || loadingAction === 'complete' }}
          disabled={groupExpansion.allCollapsed || groupExpansion.captureBusy || clientControlActive || loadingAction === 'complete'}
          onPress={() => formRef.current?.setAllGroupsExpanded(false)}
          style={[styles.editorReturn, (windowWidth < 360 || fontScale > 1.25) && styles.editorReturnCompact, (groupExpansion.allCollapsed || groupExpansion.captureBusy || clientControlActive || loadingAction === 'complete') && styles.actionButtonDisabled]}>
          <Ionicons name="contract-outline" size={18} color={theme.colors.primary.default} />
          <Text style={styles.editorReturnText}>Recolher</Text>
        </TouchableOpacity>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel="Expandir"
          accessibilityState={{ disabled: groupExpansion.allExpanded || groupExpansion.captureBusy || clientControlActive || loadingAction === 'complete' }}
          disabled={groupExpansion.allExpanded || groupExpansion.captureBusy || clientControlActive || loadingAction === 'complete'}
          onPress={() => formRef.current?.setAllGroupsExpanded(true)}
          style={[styles.editorReturn, (windowWidth < 360 || fontScale > 1.25) && styles.editorReturnCompact, (groupExpansion.allExpanded || groupExpansion.captureBusy || clientControlActive || loadingAction === 'complete') && styles.actionButtonDisabled]}>
          <Ionicons name="expand-outline" size={18} color={theme.colors.primary.default} />
          <Text style={styles.editorReturnText}>Expandir</Text>
        </TouchableOpacity>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel="Concluir checklist" onPress={handleComplete}
          disabled={checklist.status !== 'in_progress' || loadingAction === 'complete' || pending || clientControlActive || !canEditChecklist}
          style={[styles.editorComplete, (windowWidth < 360 || fontScale > 1.25) && styles.editorReturnCompact, (checklist.status !== 'in_progress' || loadingAction === 'complete' || pending || clientControlActive || !canEditChecklist) && styles.actionButtonDisabled]}>
          {loadingAction === 'complete' ? <ActivityIndicator color={theme.colors.text.inverse} size="small" /> : <Ionicons name="checkmark-circle-outline" size={20} color={theme.colors.text.inverse} />}
          <Text style={styles.editorCompleteText}>{checklist.status === 'done' ? 'Concluído' : 'Concluir'}</Text>
        </TouchableOpacity>
      </View>
      {editorMenu && <View style={styles.editorMenuOverlay}>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel="Fechar menu" style={StyleSheet.absoluteFill} onPress={() => setEditorMenu(null)} />
        <View style={[styles.editorMenuCard, { paddingBottom: Math.max(theme.spacing.lg, safeAreaInsets.bottom) }]} accessibilityViewIsModal>
          <View style={styles.editorMenuHeading}><Text style={styles.editorTitle}>{editorMenu === 'actions' ? 'Ações do checklist' : 'Menu do app'}</Text>
            <TouchableOpacity accessibilityRole="button" accessibilityLabel="Fechar menu" style={styles.editorBack} onPress={() => setEditorMenu(null)}><Ionicons name="close" size={24} color={theme.colors.text.secondary} /></TouchableOpacity>
          </View>
          <ScrollView keyboardShouldPersistTaps="handled">
            {editorMenu === 'actions' ? <>
              {checklist.status === 'in_progress' && canEditChecklist && !partner.state?.touched && menuAction('Cliente preencher', 'qr-code-outline', handleOpenShareModal, shareCreating || shareRevoking)}
              {(partner.state?.enabled || partner.state?.active || partner.state?.touched) && currentUserType === 'admin' &&
                (checklist.status === 'in_progress' || partner.state?.touched) &&
                menuAction(checklist.status === 'done' ? 'Acessos de parceiro e comprovantes' : 'Parceiro preencher por link', 'link-outline', () => setPartnerModalVisible(true), !isOnline)}
              {menuAction('Gerar PDF', 'document-text-outline', handleGeneratePdf, loadingAction === 'pdf')}
              {canOpenTransfer && menuAction('Transferir checklist', 'swap-horizontal-outline', handleOpenTransferModal, transferBusy)}
              {checklist.status === 'done' && menuAction('Reabrir checklist', 'refresh-circle-outline', handleReopenChecklist, loadingAction === 'reopen' || !canReopenChecklist)}
              {menuAction('Excluir checklist', 'trash-outline', handleDelete, loadingAction === 'delete' || !canDeleteChecklist, true)}
            </> : <>
              {navigationItem('Início', 'home-outline', '/(app)')}
              {showDashboard && navigationItem('Painel', 'grid-outline', '/(app)/admin')}
              {showDeveloper && navigationItem('Dev', 'construct-outline', '/(app)/painel-dev')}
              {navigationItem('Config', 'settings-outline', '/(app)/settings')}
              {menuAction('Ajuda', 'logo-whatsapp', () => { const url = APP_CONSTANTS.CONTACTS?.WHATSAPP_URL; if (url) void Linking.openURL(url).catch(error => IdChecklistLogger.Main.error('[Ajuda] Falha ao abrir WhatsApp', error)); })}
            </>}
          </ScrollView>
        </View>
      </View>}
      <PartnerChecklistModal visible={partnerModalVisible} scope={partnerScope} observedState={partner.state}
        prepare={preparePartnerShare} onClose={() => setPartnerModalVisible(false)} onChanged={partner.refresh} />
      <Modal
        visible={isShareFlowModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => {
          if (shareFlowModalKind === 'share') {
            handleCloseShareModal();
            return;
          }
          if (shareFlowModalKind === 'client_completed') {
            handleCloseClientCompletionModal();
            return;
          }
          IdChecklistLogger.Main.info('[shareFlowModal] onRequestClose ignorado no estado client_control');
        }}
      >
        {/* Fluxo de QR consolidado em um unico Modal nativo para evitar apresentacao concorrente no iOS. */}
        {shareFlowModalKind === 'share' ? (
          <View style={styles.shareModalBackdrop}>
            <View style={styles.shareModalCard}>
              <View style={styles.shareHeaderRow}>
                <Text style={styles.shareTitle}>Compartilhar Checklist</Text>
                <TouchableOpacity
                  onPress={handleCloseShareModal}
                  accessibilityRole="button"
                  accessibilityLabel="Fechar modal de compartilhamento"
                >
                  <Ionicons name="close-circle" size={28} color={theme.colors.text.secondary} />
                </TouchableOpacity>
              </View>

              <Text style={styles.shareSubtitle}>
                Gere um QR Code para o cliente preencher sem login.
              </Text>

              {shareCopyFeedbackVisible ? (
                <View pointerEvents="none" style={styles.shareModalFeedbackOverlay}>
                  <View style={styles.shareCopyFeedbackBadge}>
                    <Ionicons name="checkmark-circle" size={16} color={theme.colors.text.inverse} />
                    <Text style={styles.shareCopyFeedbackText}>Link copiado</Text>
                  </View>
                </View>
              ) : null}

              {!(canCollapseShareScope && shareScopeCollapsed) ? (
                <View style={styles.shareScopeCard}>
                  <View style={styles.shareScopeHeader}>
                    <Text style={styles.shareScopeTitle}>O que o cliente poderá preencher</Text>
                    {canCollapseShareScope ? (
                      <TouchableOpacity
                        style={styles.shareScopeCollapseButton}
                        onPress={() => setShareScopeCollapsed(true)}
                      >
                        <Ionicons
                          name="chevron-up"
                          size={14}
                          color={theme.colors.text.secondary}
                        />
                        <Text style={styles.shareScopeCollapseButtonText}>Minimizar</Text>
                      </TouchableOpacity>
                    ) : null}
                  </View>

                  <View style={styles.shareScopeActionsRow}>
                    <TouchableOpacity
                      style={styles.shareScopeActionButton}
                      onPress={handleSelectAllShareScopeFields}
                      disabled={shareScopeTotalFields === 0}
                    >
                      <Text style={styles.shareScopeActionButtonText}>Selecionar todos</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.shareScopeActionButton}
                      onPress={handleClearShareScopeSelection}
                      disabled={shareScopeTotalFields === 0}
                    >
                      <Text style={styles.shareScopeActionButtonText}>Limpar seleção</Text>
                    </TouchableOpacity>
                  </View>

                  {shareScopeGroups.length > 0 ? (
                    <ScrollView
                      style={styles.shareScopeGroupsList}
                      nestedScrollEnabled
                      showsVerticalScrollIndicator={false}
                    >
                      {shareScopeGroups.map(group => {
                        const isExpanded = !!shareScopeExpandedGroups[group.grupo];
                        const selectedInGroup = group.fields.filter(field => shareSelectedFieldIdsSet.has(field.id)).length;
                        const isGroupFullySelected = selectedInGroup === group.fields.length;

                        return (
                          <View key={group.grupo} style={styles.shareScopeGroupCard}>
                            <View style={styles.shareScopeGroupHeader}>
                              <TouchableOpacity
                                style={styles.shareScopeGroupTitleWrapper}
                                onPress={() => toggleShareScopeGroupExpanded(group.grupo)}
                              >
                                <Ionicons
                                  name={isExpanded ? 'chevron-down' : 'chevron-forward'}
                                  size={16}
                                  color={theme.colors.text.secondary}
                                />
                                <Text style={styles.shareScopeGroupTitle}>{group.grupo}</Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={styles.shareScopeGroupToggle}
                                onPress={() => {
                                  setShareScopeGroupSelection(group, !isGroupFullySelected);
                                }}
                              >
                                <Ionicons
                                  name={isGroupFullySelected ? 'checkbox' : 'square-outline'}
                                  size={18}
                                  color={theme.colors.primary.default}
                                />
                                <Text style={styles.shareScopeGroupToggleText}>
                                  {selectedInGroup}/{group.fields.length}
                                </Text>
                              </TouchableOpacity>
                            </View>

                            {isExpanded && (
                              <View style={styles.shareScopeFieldsList}>
                                {group.fields.map(field => {
                                  const isSelected = shareSelectedFieldIdsSet.has(field.id);
                                  return (
                                    <ChecklistFieldAccess key={field.id} name={field.nome}
                                      value={isSelected ? 'edit' : 'hidden'} allowRead={false}
                                      disabled={shareCreating || shareRevoking}
                                      onChange={value => {
                                        if ((value === 'edit') !== isSelected) toggleShareScopeFieldSelection(field.id);
                                      }} />
                                  );
                                })}
                              </View>
                            )}
                          </View>
                        );
                      })}
                    </ScrollView>
                  ) : (
                    <Text style={styles.shareScopeEmptyText}>
                      Nenhum campo compatível com edição pública foi encontrado neste checklist.
                    </Text>
                  )}

                  {shareScopeError ? <Text style={styles.shareScopeErrorText}>{shareScopeError}</Text> : null}
                </View>
              ) : null}

              {shareCreating && !shareSession ? (
                <View style={styles.shareLoadingContainer}>
                  <ActivityIndicator size="large" color={theme.colors.primary.default} />
                  <Text style={styles.shareLoadingText}>Gerando link seguro...</Text>
                </View>
              ) : shareSession ? (
                <ScrollView
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={styles.shareContent}
                >
                  <View style={[styles.shareStatusRow, isShareStatusCompact && styles.shareStatusRowCompact]}>
                    <View style={[styles.shareStatusBadge, shareStatusBadgeStyle]}>
                      <Text style={styles.shareStatusText}>{shareStatusLabel}</Text>
                    </View>
                    <View style={[styles.shareStatusActions, isShareStatusCompact && styles.shareStatusActionsCompact]}>
                      {canCollapseShareScope && shareScopeCollapsed ? (
                        <TouchableOpacity
                          style={[
                            styles.shareSmallButton,
                            styles.shareSmallEditButton,
                            isShareStatusCompact && styles.shareSmallButtonCompact,
                            isShareStatusCompact && styles.shareSmallEditButtonCompact,
                          ]}
                          onPress={() => setShareScopeCollapsed(false)}
                        >
                          <MaterialCommunityIcons
                            name="pencil-outline"
                            size={isShareStatusCompact ? 13 : 14}
                            color={theme.colors.primary.default}
                          />
                          <Text style={[styles.shareSmallButtonText, isShareStatusCompact && styles.shareSmallButtonTextCompact]}>
                            {isShareStatusCompact ? 'Editar' : 'Editar campos'}
                          </Text>
                        </TouchableOpacity>
                      ) : null}
                      <TouchableOpacity
                        style={[
                          styles.shareSmallButton,
                          isShareStatusCompact && styles.shareSmallButtonCompact,
                          shareRefreshing && styles.shareSmallButtonDisabled,
                        ]}
                        onPress={() => {
                          void handleRefreshShareStatus(false);
                        }}
                        disabled={shareRefreshing}
                      >
                        {shareRefreshing ? (
                          <ActivityIndicator size="small" color={theme.colors.primary.default} />
                        ) : (
                          <Text style={[styles.shareSmallButtonText, isShareStatusCompact && styles.shareSmallButtonTextCompact]}>
                            Atualizar
                          </Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  </View>

                  {isShareSessionActive ? (
                    <>
                      <View style={styles.shareQrContainer}>
                        <QRCode value={shareSession.url} size={196} />
                      </View>

                      <View style={styles.sharePrimaryActions}>
                        <TouchableOpacity
                          style={styles.sharePrimaryButton}
                          onPress={() => {
                            void handleCopyShareUrl();
                          }}
                        >
                          <Text style={styles.sharePrimaryButtonText}>Copiar link</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.shareWhatsAppButton}
                          onPress={() => {
                            void handleShareViaWhatsApp();
                          }}
                        >
                          <MaterialCommunityIcons name="whatsapp" size={18} color={theme.colors.text.inverse} />
                          <Text style={styles.shareWhatsAppButtonText}>Compartilhar no WhatsApp</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            styles.shareSecondaryButton,
                            (shareCreating || !canCreateShareSession) && styles.shareSecondaryButtonDisabled,
                          ]}
                          onPress={() => {
                            void handleCreateShareSession();
                          }}
                          disabled={shareCreating || !canCreateShareSession}
                        >
                          {shareCreating ? (
                            <ActivityIndicator size="small" color={theme.colors.text.primary} />
                          ) : (
                            <Text style={styles.shareSecondaryButtonText}>Gerar novo link</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    </>
                  ) : (
                    <View style={styles.shareTerminalCard}>
                      <Ionicons name="information-circle-outline" size={18} color={theme.colors.text.secondary} />
                      <Text style={styles.shareTerminalText}>{shareTerminalDescription}</Text>
                      <TouchableOpacity
                        style={[
                          styles.sharePrimaryButton,
                          styles.shareTerminalActionButton,
                          (shareCreating || !canCreateShareSession) && styles.sharePrimaryButtonDisabled,
                        ]}
                        onPress={() => {
                          void handleCreateShareSession();
                        }}
                        disabled={shareCreating || !canCreateShareSession}
                      >
                        {shareCreating ? (
                          <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                        ) : (
                          <Text style={styles.sharePrimaryButtonText}>Gerar novo link</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  )}

                  <View style={styles.shareDetailsCard}>
                    <View style={styles.shareDetailRow}>
                      <Text style={styles.shareDetailLabel}>Tempo restante</Text>
                      <Text style={styles.shareDetailValue}>{formatCountdown(shareSecondsRemaining)}</Text>
                    </View>
                    <View style={styles.shareDetailRow}>
                      <Text style={styles.shareDetailLabel}>Última atividade</Text>
                      <Text style={styles.shareDetailValue}>{formatDateTime(shareSession.lastActivityAt)}</Text>
                    </View>
                    <View style={styles.shareDetailRow}>
                      <Text style={styles.shareDetailLabel}>Atualizações</Text>
                      <Text style={styles.shareDetailValue}>
                        {shareSession.updatesCount}
                        {shareSession.maxUpdates !== null ? ` / ${shareSession.maxUpdates}` : ''}
                      </Text>
                    </View>
                    <View style={styles.shareDetailRow}>
                      <Text style={styles.shareDetailLabel}>Escopo do link</Text>
                      <Text style={styles.shareDetailValue}>
                        {shareSessionScopeMode === 'custom_fields'
                          ? `${shareSessionScopeCount}/${shareScopeTotalFields} campos`
                          : `Completo (${shareScopeTotalFields})`}
                      </Text>
                    </View>
                    <View style={styles.shareDetailRow}>
                      <Text style={styles.shareDetailLabel}>Expira em</Text>
                      <Text style={styles.shareDetailValue}>{formatDateTime(shareSession.expiresAt)}</Text>
                    </View>
                  </View>

                  {shareError ? <Text style={styles.shareErrorText}>{shareError}</Text> : null}

                  {isShareSessionActive && (
                    <TouchableOpacity
                      style={[
                        styles.shareDangerButton,
                        (shareRevoking || shareEffectiveStatus !== 'active') && styles.shareDangerButtonDisabled,
                      ]}
                      onPress={() => {
                        void handleRevokeShareSession();
                      }}
                      disabled={shareRevoking || shareEffectiveStatus !== 'active'}
                    >
                      {shareRevoking ? (
                        <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                      ) : (
                        <Text style={styles.shareDangerButtonText}>Encerrar acesso</Text>
                      )}
                    </TouchableOpacity>
                  )}
                </ScrollView>
              ) : (
                <View style={styles.shareLoadingContainer}>
                  <Text style={styles.shareLoadingText}>Nenhuma sessão de compartilhamento ativa.</Text>
                  <TouchableOpacity
                    style={[
                      styles.sharePrimaryButton,
                      styles.shareLoadingPrimaryButton,
                      !canCreateShareSession && styles.sharePrimaryButtonDisabled,
                    ]}
                    onPress={() => {
                      void handleCreateShareSession();
                    }}
                    disabled={!canCreateShareSession}
                  >
                    <Text style={styles.sharePrimaryButtonText}>Gerar link</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
        ) : shareFlowModalKind === 'client_control' ? (
          <View style={styles.clientControlModalBackdrop}>
            <View style={styles.clientControlModalCard}>
              <View style={styles.clientControlHeader}>
                <Ionicons name="alert-circle-outline" size={24} color={theme.colors.util.warning} />
                <Text style={styles.clientControlTitle}>Cliente preenchendo checklist</Text>
              </View>

              <Text style={styles.clientControlDescription}>
                {clientActivityMessage ||
                  'Detectamos atividade do cliente no link público. A edição local foi bloqueada para evitar conflito de dados.'}
              </Text>

              <View style={styles.clientControlInfoCard}>
                <View style={styles.clientControlInfoRow}>
                  <Text style={styles.clientControlInfoLabel}>Status do link</Text>
                  <Text style={styles.clientControlInfoValue}>{shareStatusLabel}</Text>
                </View>
                <View style={styles.clientControlInfoRow}>
                  <Text style={styles.clientControlInfoLabel}>Última atividade</Text>
                  <Text style={styles.clientControlInfoValue}>{formatDateTime(shareSession?.lastActivityAt || null)}</Text>
                </View>
                <View style={styles.clientControlInfoRow}>
                  <Text style={styles.clientControlInfoLabel}>Tempo restante</Text>
                  <Text style={styles.clientControlInfoValue}>{formatCountdown(shareSecondsRemaining)}</Text>
                </View>
              </View>

              <View style={styles.clientControlActions}>
                <TouchableOpacity
                  style={[styles.clientControlPrimaryButton, clientControlBusy && styles.clientControlPrimaryButtonDisabled]}
                  onPress={() => {
                    void handleRetakeChecklistControl();
                  }}
                  disabled={clientControlBusy}
                >
                  {clientControlBusy ? (
                    <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                  ) : (
                    <Text style={styles.clientControlPrimaryButtonText}>Retomar controle</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ) : shareFlowModalKind === 'client_completed' ? (
          <View style={styles.clientControlModalBackdrop}>
            <View style={styles.clientControlModalCard}>
              <View style={styles.clientControlHeader}>
                <Ionicons name="checkmark-done-outline" size={24} color={theme.colors.util.success} />
                <Text style={styles.clientControlTitle}>Cliente concluiu as alterações</Text>
              </View>

              <Text style={styles.clientControlDescription}>
                {clientCompletionMessage || 'Cliente concluiu as alterações do checklist.'}
              </Text>

              <View style={styles.clientControlActions}>
                <TouchableOpacity
                  style={styles.clientControlPrimaryButton}
                  onPress={handleCloseClientCompletionModal}
                  accessibilityRole="button"
                  accessibilityLabel="Voltar para edição do checklist"
                >
                  <Text style={styles.clientControlPrimaryButtonText}>Voltar para edição</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ) : null}
      </Modal>
      <Modal
        visible={transferModalKind !== null}
        transparent
        animationType="slide"
        onRequestClose={handleCloseTransferModal}
      >
        <View style={styles.shareModalBackdrop}>
          <View style={styles.shareModalCard}>
            <View style={styles.shareHeaderRow}>
              <View>
                <Text style={styles.shareTitle}>
                  {transferModalKind === 'confirm' ? 'Confirmar transferência?' : 'Transferir checklist'}
                </Text>
                <Text style={styles.shareSubtitle}>
                  {transferModalKind === 'confirm'
                    ? `${transferOwnerName} → ${transferTarget?.name?.trim() || 'responsável'}`
                    : `Responsável atual: ${transferOwnerName}`}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.shareSmallButton}
                onPress={handleCloseTransferModal}
                disabled={transferBusy}
                accessibilityRole="button"
                accessibilityLabel="Fechar transferência"
              >
                <Ionicons name="close" size={20} color={theme.colors.text.secondary} />
              </TouchableOpacity>
            </View>

            {transferModalKind === 'list' ? (
              transferCandidates.length > 0 ? (
                <ScrollView
                  style={styles.transferCandidatesList}
                  keyboardShouldPersistTaps="handled"
                  showsVerticalScrollIndicator={false}
                >
                  {transferCandidates.map(candidate => {
                    const candidateName = getTransferRecipientName(candidate);
                    return (
                      <TouchableOpacity
                        key={candidate.id}
                        style={styles.transferCandidateRow}
                        onPress={() => handleSelectTransferTarget(candidate)}
                        disabled={transferBusy}
                        accessibilityRole="button"
                        accessibilityLabel={`Transferir para ${candidateName}`}
                      >
                        <View style={styles.transferCandidateCopy}>
                          <Text style={styles.transferCandidateName} numberOfLines={1} ellipsizeMode="tail">
                            {candidateName}
                          </Text>
                          <Text style={styles.transferCandidateRole}>
                            {getTransferRecipientRoleLabel(candidate.user_type)}
                          </Text>
                        </View>
                        <Ionicons name="chevron-forward" size={18} color={theme.colors.text.secondary} />
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : (
                <Text style={styles.transferEmptyText}>Nenhum outro responsável elegível foi encontrado.</Text>
              )
            ) : (
              <>
                <View style={styles.shareTerminalCard}>
                  <Text style={styles.shareTerminalText}>
                    {checklist.status === 'done'
                      ? 'O checklist continuará concluído. O novo responsável poderá visualizá-lo e decidir separadamente se deve reabrir pelo fluxo existente.'
                      : 'Confirme se as últimas alterações do responsável atual já foram sincronizadas antes de transferir.'}
                  </Text>
                </View>
                {transferError ? <Text style={styles.shareErrorText}>{transferError}</Text> : null}
                <View style={styles.transferModalActions}>
                  <TouchableOpacity
                    style={[styles.shareSecondaryButton, styles.transferActionButton]}
                    onPress={() => {
                      if (!transferBusy) setTransferModalKind('list');
                    }}
                    disabled={transferBusy}
                  >
                    <Text style={styles.shareSecondaryButtonText}>Voltar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.sharePrimaryButton,
                      styles.transferActionButton,
                      transferBusy && styles.sharePrimaryButtonDisabled,
                    ]}
                    onPress={() => { void handleTransferChecklist(); }}
                    disabled={transferBusy}
                  >
                    {transferBusy ? (
                      <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                    ) : (
                      <Text style={styles.sharePrimaryButtonText}>Transferir checklist</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            )}
            {transferModalKind === 'list' && transferError ? (
              <Text style={styles.shareErrorText}>{transferError}</Text>
            ) : null}
          </View>
        </View>
      </Modal>
      {pdfBusy && (
        <View style={styles.overlay} pointerEvents="auto">
          <ActivityIndicator size="large" color="#fff" />
          <Text style={styles.overlayText}>Gerando PDF…</Text>
        </View>
      )}
    </View>
  );
});

const ChecklistEditorScreen = observer(() => {
  const subscriptionSnapshot = globalState.subscription.get();
  const profileSnapshot = globalState.user.profile.get();
  const premiumAccess = hasPremiumAccess(subscriptionSnapshot, profileSnapshot);

  if (!premiumAccess) {
    return (
      <View style={styles.restrictedWrapper}>
        <UpgradePrompt variant="restricted" />
      </View>
    );
  }

  return <ChecklistEditorContent />;
});

const styles = StyleSheet.create({
  editorHeader: { borderBottomWidth: 1, borderBottomColor: theme.colors.border.light, paddingHorizontal: 14, paddingBottom: theme.spacing.sm },
  editorTopRow: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.xs },
  editorBack: { width: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  editorTitleArea: { flex: 1, minWidth: 0 },
  editorTitle: { fontSize: 15, lineHeight: 21, fontWeight: theme.fontWeights.bold as '700', color: theme.colors.text.primary },
  editorLocalState: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  editorStateText: { fontSize: 10, lineHeight: 12, color: theme.colors.text.secondary, flexShrink: 1, textAlign: 'right' },
  editorTool: { minWidth: 50, minHeight: 44, paddingHorizontal: 5, alignItems: 'center', justifyContent: 'center', gap: 3 },
  editorToolText: { fontSize: theme.components.checklistEditor.fontSizes.supporting, color: theme.colors.text.secondary },
  editorResponsible: { fontSize: 10, lineHeight: 12, color: theme.colors.text.secondary, textAlign: 'right' },
  editorStatusRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 4 },
  editorStatusBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12, borderWidth: 1, borderColor: '#FDE68A', backgroundColor: '#FEF3C7' },
  editorStatusDone: { borderColor: '#BBF7D0', backgroundColor: '#DCFCE7' },
  editorStatusText: { fontSize: 12, lineHeight: 16, fontWeight: '600', color: '#92400E' }, editorStatusDoneText: { color: '#15803D' },
  editorMetadata: { flex: 1, minWidth: 0, alignItems: 'flex-end' },
  editorFooter: { flexDirection: 'row', alignItems: 'stretch', gap: theme.spacing.sm, padding: 10, borderTopWidth: 1, borderTopColor: theme.colors.border.light, backgroundColor: theme.colors.background.primary },
  editorReturn: { flex: 1, minWidth: 0, minHeight: 48, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: theme.components.checklistEditor.spacing.compactGap, paddingHorizontal: theme.components.checklistEditor.spacing.footerButtonHorizontal, borderWidth: 1, borderColor: theme.colors.border.light, borderRadius: theme.components.checklistEditor.borderRadius.footerButton },
  editorReturnCompact: { flexDirection: 'column', gap: 2, paddingVertical: theme.spacing.xs },
  editorReturnCopy: { flexShrink: 1 },
  editorReturnText: { fontSize: theme.fontSizes.xs, fontWeight: theme.fontWeights.semibold as '600', textAlign: 'center', color: theme.colors.primary.default },
  editorReturnGroup: { fontSize: theme.fontSizes.xxs, lineHeight: 14, textAlign: 'center', color: theme.colors.text.secondary },
  editorProgress: { flex: 1, minWidth: 0, justifyContent: 'center' },
  editorProgressText: { fontSize: theme.components.checklistEditor.fontSizes.supporting, lineHeight: 16, textAlign: 'center', color: theme.colors.text.tertiary },
  editorComplete: { flex: 1, minWidth: 0, minHeight: 48, borderRadius: theme.components.checklistEditor.borderRadius.footerButton, paddingHorizontal: theme.components.checklistEditor.spacing.footerButtonHorizontal, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: theme.components.checklistEditor.spacing.compactGap, backgroundColor: theme.components.checklistEditor.completeBackground },
  editorCompleteText: { fontSize: theme.fontSizes.xs, fontWeight: theme.fontWeights.semibold as '600', color: theme.colors.text.inverse },
  editorMenuOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: theme.zIndex.modal, justifyContent: 'flex-end', backgroundColor: 'rgba(30,45,70,0.30)' },
  editorMenuCard: { maxHeight: '80%', padding: 18, paddingBottom: theme.spacing.lg, borderTopLeftRadius: theme.borderRadius.xl, borderTopRightRadius: theme.borderRadius.xl, backgroundColor: theme.colors.background.primary },
  editorMenuHeading: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing.sm },
  editorMenuRow: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 48, padding: theme.spacing.sm, borderBottomWidth: 1, borderBottomColor: theme.colors.border.light },
  editorMenuLabel: { flex: 1, fontSize: theme.fontSizes.sm, color: theme.colors.text.primary },

  restrictedWrapper: {
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.background.secondary,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  topBar: {
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  statusContainer: {
    marginLeft: theme.spacing.sm,
    minHeight: 24,
    minWidth: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionsGroup: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    flexShrink: 1,
  },
  actionButton: {
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  actionButtonDisabled: {
    opacity: 0.6,
  },
  actionLabel: {
    marginTop: theme.spacing.xs,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
    color: theme.colors.text.primary,
    textAlign: 'center',
  },
  groupsBar: {
    marginBottom: theme.spacing.sm,
  },
  groupsContent: {
    paddingVertical: theme.spacing.xs,
    paddingRight: theme.spacing.sm,
  },
  groupChip: {
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.primary,
    marginRight: theme.spacing.sm,
  },
  groupChipActive: {
    backgroundColor: theme.colors.primary.light,
    borderColor: theme.colors.primary.default,
  },
  groupChipLabel: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  groupChipLabelActive: {
    color: theme.colors.primary.contrast,
  },
  metaSingleLine: {
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
    color: theme.colors.text.secondary,
  },
  transferMetaLine: {
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  transferButton: {
    height: 24,
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    paddingHorizontal: 6,
    borderWidth: 1,
    borderColor: '#C8D5FA',
    borderRadius: 8,
    backgroundColor: '#F7F9FF',
  },
  transferButtonDisabled: {
    opacity: 0.6,
  },
  transferButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.xs,
    fontWeight: '800',
  },
  transferCandidatesList: {
    maxHeight: 360,
  },
  transferCandidateRow: {
    minHeight: 58,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    paddingVertical: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
  },
  transferCandidateCopy: {
    flex: 1,
    minWidth: 0,
  },
  transferCandidateName: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
  transferCandidateRole: {
    marginTop: 2,
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.xs,
  },
  transferEmptyText: {
    paddingVertical: theme.spacing.lg,
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    textAlign: 'center',
  },
  transferModalActions: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.lg,
  },
  transferActionButton: {
    flex: 1,
  },
  clientControlBanner: {
    marginBottom: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: '#F4D18A',
    backgroundColor: '#FFF8E8',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  clientControlBannerContent: {
    flex: 1,
  },
  clientControlBannerTitle: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
  clientControlBannerSubtitle: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    marginTop: 2,
  },
  placeholderText: {
    fontSize: 18,
    color: theme.colors.text.primary,
    marginTop: 32,
    textAlign: 'center',
  },
  retryButton: {
    alignSelf: 'center',
    marginTop: theme.spacing.lg,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.primary.default,
  },
  retryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
  },
  formContainer: {
    flex: 1,
    marginTop: theme.spacing.sm,
  },
  formContainerLocked: {
    opacity: 0.6,
  },
  overlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 50,
  },
  overlayText: {
    marginTop: 12,
    color: '#fff',
    fontSize: 16,
  },
  shareModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.lg,
  },
  shareModalCard: {
    position: 'relative',
    width: '100%',
    maxWidth: 520,
    maxHeight: '92%',
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.lg,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  shareModalFeedbackOverlay: {
    position: 'absolute',
    top: theme.spacing.xl + theme.spacing.xs,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 12,
    elevation: 12,
  },
  shareHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  shareTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.primary,
  },
  shareSubtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.md,
  },
  shareScopeCard: {
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary,
    padding: theme.spacing.sm,
    marginBottom: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  shareScopeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  shareScopeTitle: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
  shareScopeCollapseButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    paddingHorizontal: theme.spacing.xs,
    paddingVertical: 4,
    backgroundColor: theme.colors.background.primary,
  },
  shareScopeCollapseButtonText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.xs,
    fontWeight: '600',
  },
  shareScopeActionsRow: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
  },
  shareScopeActionButton: {
    flex: 1,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    paddingVertical: 7,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background.primary,
  },
  shareScopeActionButtonText: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.xs,
    fontWeight: '600',
  },
  shareScopeGroupsList: {
    maxHeight: 300,
  },
  shareScopeGroupCard: {
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.background.primary,
    paddingVertical: 6,
    paddingHorizontal: 8,
    marginBottom: theme.spacing.xs,
  },
  shareScopeGroupHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  shareScopeGroupTitleWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flex: 1,
  },
  shareScopeGroupTitle: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  shareScopeGroupToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  shareScopeGroupToggleText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.xs,
    fontWeight: '600',
  },
  shareScopeFieldsList: {
    marginTop: theme.spacing.xs,
    paddingTop: theme.spacing.xs,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
    gap: 6,
  },
  shareScopeEmptyText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    textAlign: 'center',
    paddingVertical: theme.spacing.sm,
  },
  shareScopeErrorText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.xs,
    fontWeight: '600',
  },
  shareLoadingContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.lg,
    gap: theme.spacing.sm,
  },
  shareLoadingPrimaryButton: {
    width: '100%',
    marginTop: theme.spacing.xs,
  },
  shareLoadingText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    textAlign: 'center',
  },
  shareContent: {
    paddingBottom: theme.spacing.sm,
  },
  shareStatusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  shareStatusRowCompact: {
    alignItems: 'flex-start',
  },
  shareStatusActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    flexShrink: 1,
    marginLeft: theme.spacing.sm,
  },
  shareStatusActionsCompact: {
    gap: 4,
  },
  shareStatusBadge: {
    borderRadius: theme.borderRadius.md,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.sm,
  },
  shareStatusBadgeActive: {
    backgroundColor: '#E5F9EE',
  },
  shareStatusBadgeRevoked: {
    backgroundColor: '#FDECEC',
  },
  shareStatusBadgeExpired: {
    backgroundColor: '#FFF4E5',
  },
  shareStatusBadgeCompleted: {
    backgroundColor: '#EEF2FF',
  },
  shareStatusText: {
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
    color: theme.colors.text.primary,
  },
  shareSmallButton: {
    minWidth: 90,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    paddingVertical: 8,
    paddingHorizontal: theme.spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 4,
  },
  shareSmallButtonCompact: {
    minWidth: 76,
    paddingVertical: 7,
    paddingHorizontal: 8,
  },
  shareSmallEditButton: {
    minWidth: 128,
  },
  shareSmallEditButtonCompact: {
    minWidth: 98,
  },
  shareSmallButtonDisabled: {
    opacity: 0.6,
  },
  shareSmallButtonText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  shareSmallButtonTextCompact: {
    fontSize: theme.fontSizes.xs,
  },
  shareQrContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    backgroundColor: '#fff',
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    marginBottom: theme.spacing.md,
  },
  shareCopyFeedbackBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(18, 103, 56, 0.95)',
  },
  shareCopyFeedbackText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.xs,
    fontWeight: '700',
  },
  sharePrimaryActions: {
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  sharePrimaryButton: {
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.primary.default,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sharePrimaryButtonDisabled: {
    opacity: 0.6,
  },
  sharePrimaryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
  shareWhatsAppButton: {
    borderRadius: theme.borderRadius.sm,
    backgroundColor: '#25D366',
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
  },
  shareWhatsAppButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
  shareSecondaryButton: {
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    backgroundColor: theme.colors.background.secondary,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareSecondaryButtonDisabled: {
    opacity: 0.6,
  },
  shareSecondaryButtonText: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
  },
  shareDetailsCard: {
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.sm,
    marginBottom: theme.spacing.md,
    backgroundColor: theme.colors.background.secondary,
    gap: theme.spacing.xs,
  },
  shareDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  shareDetailLabel: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
  },
  shareDetailValue: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
    maxWidth: '62%',
    textAlign: 'right',
  },
  shareErrorText: {
    color: theme.colors.util.error,
    fontSize: theme.fontSizes.sm,
    marginBottom: theme.spacing.sm,
  },
  shareDangerButton: {
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.util.error,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareDangerButtonDisabled: {
    opacity: 0.55,
  },
  shareDangerButtonText: {
    color: theme.colors.text.inverse,
    fontWeight: '700',
    fontSize: theme.fontSizes.sm,
  },
  shareTerminalCard: {
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.secondary,
    padding: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  shareTerminalText: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    lineHeight: 20,
  },
  shareTerminalActionButton: {
    width: '100%',
  },
  clientControlModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
  },
  clientControlModalCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  clientControlHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  clientControlTitle: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.lg,
    fontWeight: '700',
    flex: 1,
  },
  clientControlDescription: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
    lineHeight: 20,
  },
  clientControlInfoCard: {
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    backgroundColor: theme.colors.background.secondary,
    padding: theme.spacing.sm,
    gap: theme.spacing.xs,
  },
  clientControlInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  clientControlInfoLabel: {
    color: theme.colors.text.secondary,
    fontSize: theme.fontSizes.sm,
  },
  clientControlInfoValue: {
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: '600',
    textAlign: 'right',
    maxWidth: '65%',
  },
  clientControlActions: {
    gap: theme.spacing.sm,
  },
  clientControlPrimaryButton: {
    minHeight: 46,
    borderRadius: theme.borderRadius.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.primary.default,
  },
  clientControlPrimaryButtonDisabled: {
    opacity: 0.6,
  },
  clientControlPrimaryButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: '700',
  },
});

export default ChecklistEditorScreen;
